/**
 * Finance message bundle. The orchestrator merges these into the app's
 * i18next resources; nothing here self-registers.
 *
 * Everything is namespaced under `finance` so one lane can never overwrite
 * another lane's key, and EVERY key exists in both locales — the pseudo-locale
 * CI check treats a missing Arabic string as a failure, and so does a Saudi
 * finance team. Screens still pass an English default to `t()`, so a page
 * rendered before this bundle is merged degrades to readable English rather
 * than to a raw key.
 *
 * Interpolation placeholders are identical in both locales by construction.
 */
export const financeMessages = {
  en: {
    finance: {
      aging: {
        apForbidden: "You cannot read the payables aging report.",
        apTotals: "Payables by bucket",
        arForbidden: "You cannot read the receivables aging report.",
        arTotals: "Receivables by bucket",
        asOf: "As of",
        bucket: {
          "1_30": "1–30 days",
          "31_60": "31–60 days",
          "61_90": "61–90 days",
          "90_plus": "90+ days",
          current: "Current",
        },
        currencyHint: "Required. One column may only ever hold one currency.",
        customer: "Customer",
        doesNotTieOut:
          "This report does NOT tie out to the AR control account. The difference is {{difference}} — one of the two is wrong, so do not treat these figures as authoritative.",
        run: "Run the report",
        subtitle: "Receivables and payables bucketed from the due date, one currency at a time.",
        supplier: "Supplier",
        tabAP: "Payables",
        tabAR: "Receivables",
        tiesOut: "This report ties out to the AR control account at {{balance}}.",
        title: "Aging",
        total: "Total",
      },
      assetClasses: {
        accountsMustDiffer: "The four control accounts must differ from one another.",
        accountsRequired:
          "All four control accounts are required — an unmapped one posts somewhere arbitrary.",
        accumulatedAccount: "Accumulated depreciation account",
        activeLabel: "Active",
        code: "Code",
        codeFrozen: "Immutable.",
        codeNameRequired: "An asset class needs a code and a name.",
        costAccount: "Cost account",
        create: "Create class",
        createTitle: "New asset class",
        defaultFactor: "Default declining factor",
        defaultLife: "Default useful life (months)",
        defaultMethod: "Default method",
        edit: "Edit",
        editTitle: "Edit {{code}}",
        empty: "No asset classes yet.",
        expenseAccount: "Depreciation expense account",
        gainLossAccount: "Gain or loss on disposal account",
        lifeMonths: "{{months}} months",
        listSection: "Classes",
        name: "Name",
        new: "New asset class",
        save: "Save class",
        subtitle:
          "Depreciation defaults new assets inherit, and the four control accounts every asset in the class posts through.",
        switchDefault: "Declining balance switches to straight line once it charges more",
        title: "Asset classes",
      },
      bankAccounts: {
        accountNumber: "Account number",
        accountsMustDiffer:
          "The ledger account and the clearing account must differ — otherwise reconciling proves nothing.",
        accountsRequired:
          "Choose both the general-ledger account and the in-transit clearing account.",
        activeLabel: "Active",
        bic: "BIC",
        clearingAccount: "In-transit clearing account",
        clearingAccountHint: "Where money sits until the bank actually moves it.",
        code: "Code",
        codeFrozen: "Immutable.",
        codeNameRequired: "A bank account needs a code and a name.",
        columns: {
          active: "Active",
          code: "Code",
          iban: "IBAN",
          name: "Name",
        },
        create: "Register account",
        createTitle: "New bank account",
        currencyFrozen: "Immutable.",
        editTitle: "Edit {{code}}",
        empty: "No bank accounts registered yet.",
        filterActive: "Status",
        glAccount: "Ledger account",
        glAccountHint: "Where the reconciled balance lives.",
        iban: "IBAN",
        name: "Name",
        new: "New bank account",
        save: "Save account",
        subtitle: "The company's own accounts — where statements arrive from.",
        title: "Bank accounts",
      },
      bankStatements: {
        accountRequired: "Choose the bank account.",
        bankAccount: "Bank account",
        columns: {
          bankAccount: "Bank account",
          closing: "Closing balance",
          duplicates: "Duplicates",
          fileName: "File",
          imported: "Imported",
          period: "Period",
          status: "Status",
        },
        empty: "No statements imported yet.",
        file: "Statement file",
        fileRequired: "Choose the statement file.",
        fileTooLarge: "A statement file may be at most 16 MiB.",
        fileUnreadable: "That file could not be read.",
        filterBankAccount: "Filter by account",
        format: "Format",
        formatHint:
          "CAMT.053 and OFX describe themselves; CSV is only as trustworthy as its profile.",
        formatOption: {
          camt053: "CAMT.053",
          csv: "CSV",
          ofx: "OFX",
        },
        gapDetected:
          "The opening balance does not continue this account's stored closing balance. Movements are missing.",
        gapDetectedExpected:
          "The opening balance does not continue this account's stored closing balance of {{expected}}. Movements are missing.",
        import: "Import statement",
        importSection: "Import a statement",
        importSummary: "{{imported}} movements imported, {{duplicates}} already recorded.",
        profile: "CSV dialect",
        profileDefault: "The account's active profile",
        statusOption: {
          gap_detected: "Gap detected",
          imported: "Imported",
        },
        subtitle: "Imported statement files, their continuity and what they contributed.",
        title: "Bank statements",
      },
      common: {
        active: "Active",
        all: "All",
        branchRequired: "Select an acting branch before creating an operational document.",
        choose: "Choose…",
        currency: "Currency",
        fxRate: "Exchange rate",
        fxRateHint: "Base-currency units per document-currency unit.",
        fxRateInvalid: "Enter a positive exchange rate.",
        inactive: "Inactive",
      },
      customerInvoices: {
        addLine: "Add line",
        allocatedTotal: "Allocated",
        backToList: "Back to customer invoices",
        columns: {
          customer: "Customer",
          dueDate: "Due",
          grossTotal: "Gross",
          invoiceNo: "Number",
          issueDate: "Issued",
          openAmount: "Open",
          status: "Status",
        },
        create: "New invoice",
        createCreditNote: "Create draft credit note",
        createDraft: "Create draft invoice",
        createSubtitle:
          "Creates a draft. The legal series number is allocated when you finalize it.",
        createTitle: "New customer invoice",
        creditIssueDate: "Issue date",
        creditLinesRequired: "Credit at least one invoice line.",
        creditNoteDraft: "Draft credit note",
        creditNoteHint:
          "Only the credited quantity is your input. Description, price, revenue account and the tax snapshot are copied from the invoice line.",
        creditNoteSection: "Credit note",
        creditNoteStatus: {
          draft: "draft",
          posted: "posted",
          void: "void",
        },
        creditQuantity: "Credit qty",
        creditQuantityInvalid:
          "Line {{line}}: enter a non-zero quantity with at most six decimals.",
        creditQuantityLine: "Credit line {{line}}",
        creditReason: "Reason",
        creditReasonRequired: "A credit note needs a written reason.",
        creditedTotal: "Credited",
        customer: "Customer",
        customerRequired: "Choose the customer.",
        draftTitle: "Draft invoice",
        dueBeforeIssue: "The due date cannot precede the issue date.",
        dueDate: "Due date",
        dueDateHint: "Leave empty to derive it from the customer's payment terms.",
        empty: "No customer invoices match these filters.",
        filterCustomer: "Customer",
        filterStatus: "Status",
        finalize: "Issue and post",
        finalizeCreditNote: "Issue and post the credit note",
        grossTotal: "Gross",
        headerSection: "Invoice",
        immutable: "A posted invoice is immutable. The only correction is a credit note.",
        issueDate: "Issue date",
        lineDescription: "Description",
        lineDescriptionRequired: "Line {{line}} needs a description.",
        lineHeading: "Line {{line}}",
        lineNet: "Net",
        linePriceInvalid: "Line {{line}} needs a unit price.",
        lineQuantity: "Quantity",
        lineQuantityInvalid: "Line {{line}} needs a non-zero quantity.",
        lineRevenueAccount: "Revenue account",
        lineRevenueRequired: "Line {{line}} needs a revenue account.",
        lineTax: "Tax",
        lineTaxCode: "Tax code",
        lineTaxCodeRequired:
          "Line {{line}} needs its determined tax code — the engine calculates the amount from it.",
        lineUnitPrice: "Unit price",
        linesSection: "Lines",
        loadError: "Customer invoices could not be loaded.",
        netPreview: "Net of entered lines: {{amount}} {{currency}}",
        netTotal: "Net",
        notFound: "This invoice could not be loaded.",
        notes: "Notes",
        numberAtFinalize: "Allocated at finalization",
        openAmount: "Open",
        postedAt: "Posted",
        priceInvalid: "Amounts carry at most four decimals.",
        quantityInvalid: "Quantities carry at most six decimals and cannot be zero.",
        removeLine: "Remove line {{line}}",
        status: {
          draft: "Draft",
          paid: "Paid",
          partially_paid: "Partially paid",
          posted: "Posted",
          void: "Void",
        },
        subtitle: "Drafts are editable; everything past draft is an issued legal document.",
        taxCodeHint: "The engine calculates the amount from this code and snapshots it.",
        taxPercent: "{{percent}}%",
        taxTotal: "Tax",
        title: "Customer invoices",
      },
      depreciationRuns: {
        assetCount: "{{count}} assets",
        empty: "No depreciation has been run yet.",
        execute: "Run depreciation",
        historySection: "History",
        period: "Fiscal period",
        periodRequired: "Choose the fiscal period.",
        runHint:
          "Re-running a period that already posted is safe: it finds nothing due and posts nothing.",
        runResult: "{{assets}} assets charged across {{journals}} journals.",
        runSection: "Run a period",
        runSummary: "{{assets}} assets · {{journals}} journals",
        statusOption: {
          completed: "Completed",
          failed: "Failed",
          pending: "Pending",
        },
        subtitle: "One run per fiscal period, summarized into one journal per asset class.",
        title: "Depreciation runs",
      },
      fixedAssets: {
        accumulated: "Posted depreciation",
        accumulatedAfter: "Accumulated after",
        back: "Back to the register",
        capitalizationDate: "Capitalization date",
        capitalize: "Capitalize an asset",
        capitalizeAction: "Capitalize and generate the schedule",
        capitalizeSection: "Capitalize an asset",
        charge: "Charge",
        class: "Asset class",
        classRequired: "Choose the asset class.",
        columns: {
          assetNo: "Asset",
          capitalized: "Capitalized",
          class: "Class",
          cost: "Cost",
          description: "Description",
          nbv: "Net book value",
          status: "Status",
        },
        cost: "Acquisition cost",
        costInvalid: "Enter the acquisition cost with at most four decimals.",
        currencyHint:
          "Must be the company's functional currency — assets are held at historical cost.",
        decliningFactor: "Declining factor",
        description: "Description",
        descriptionRequired: "Describe the asset.",
        disposalDate: "Disposal date",
        disposalDerecognized: "Derecognized at gross:",
        disposalGainLoss: "Gain or loss",
        disposalHint:
          "Gain or loss is proceeds minus net book value, counting only depreciation that posted. Disposal is terminal.",
        disposalSection: "Dispose of the asset",
        dispose: "Dispose and derecognize",
        disposedNotice: "This asset is disposed. That is terminal and it no longer depreciates.",
        empty: "No assets match these filters.",
        factorInvalid: "The declining factor is above 1 and at most 5.",
        filterClass: "Class",
        filterStatus: "Status",
        inherit: "Inherit from the class",
        inheritHint: "Leave empty to inherit the class default.",
        lifeInvalid: "Useful life is between 1 and 1200 months.",
        lineStatus: "Status",
        method: "Depreciation method",
        methodOption: {
          declining_balance: "Declining balance",
          straight_line: "Straight line",
        },
        nbv: "Net book value",
        newLife: "Revised total life (months)",
        newLifeHint: "The TOTAL life, including periods already posted.",
        newSalvage: "Revised salvage value",
        notFound: "This asset could not be loaded.",
        period: "Period",
        proceeds: "Proceeds",
        proceedsHint: "Zero for a scrapping.",
        proceedsInvalid: "Enter the proceeds — zero for a scrapping — with at most four decimals.",
        revise: "Revise estimates",
        revisionEmpty: "A revision has to change the useful life, the salvage value, or both.",
        revisionHint:
          "Prospective only. Periods that already posted stay exactly as they are; the undepreciated balance is spread over what remains.",
        revisionReason: "Reason",
        revisionResult:
          "{{posted}} posted periods left untouched, {{replaced}} scheduled periods regenerated.",
        revisionSection: "Revise the estimates",
        salvage: "Salvage value",
        salvageHint: "Defaults to zero. Never depreciated below it.",
        salvageInvalid: "Enter the salvage value with at most four decimals.",
        scheduleSection: "Depreciation schedule",
        scheduleStatus: {
          posted: "Posted",
          scheduled: "Scheduled",
        },
        scheduleWarning:
          "The whole schedule is generated now. If the fiscal calendar does not reach the end of the useful life the request is refused rather than truncated.",
        statusOption: {
          active: "Active",
          disposed: "Disposed",
          draft: "Draft",
        },
        subtitle: "The register, at historical cost, with depreciation that has actually posted.",
        switchToStraightLine: "Switch to straight line once it charges more",
        title: "Fixed assets",
        usefulLife: "Useful life (months)",
      },
      gateway: {
        columns: {
          capturedAt: "Captured",
          chargeId: "Charge",
          fee: "Fee",
          gross: "Gross",
          net: "Net",
          provider: "Provider",
          status: "Status",
        },
        eventId: "Gateway event",
        exceptionReason: {
          ambiguous_match: "Ambiguous match",
          amount_mismatch: "Amount mismatch",
          currency_mismatch: "Currency mismatch",
          order_not_found: "Order not found",
          provider_not_configured: "Provider not configured",
        },
        filterOpen: "Show",
        filterStatus: "Status",
        noExceptions: "Nothing is waiting on the exception queue.",
        noPayments: "No charges match these filters.",
        noPayouts: "No settlements match these filters.",
        openOnly: "Unresolved only",
        paymentStatus: {
          exception: "Exception",
          matched: "Matched",
          pending: "Pending",
          settled: "Settled",
        },
        payoutStatus: {
          pending: "Pending",
          reconciled: "Reconciled",
        },
        reconcile: "Reconcile the settlement",
        replay: "Replay",
        replayHint:
          "Re-processes a stored notification. A redelivery of the same event is a no-op.",
        replaySection: "Replay a gateway event",
        resolutionDetail: "What was done",
        resolve: "Close the exception",
        resolvedAt: "Resolved",
        statementLine: "Bank statement line",
        statementLineHint:
          "Optional. Naming it makes that line's posting the settlement's journal instead of a second one.",
        subtitle:
          "Charges, the exceptions nobody could attribute, and the settlements that carry them to the bank.",
        tabExceptions: "Exceptions",
        tabPayments: "Charges",
        tabPayouts: "Payouts",
        title: "Payment gateway",
      },
      nav: {
        aging: "Aging",
        assetClasses: "Asset classes",
        bankAccounts: "Bank accounts",
        bankStatements: "Bank statements",
        customerInvoices: "Customer invoices",
        customerReceipts: "Customer receipts",
        depreciationRuns: "Depreciation runs",
        fixedAssets: "Fixed assets",
        gateway: "Payment gateway",
        group: "Finance",
        paymentRuns: "Payment runs",
        periodClose: "Period close",
        periodEnd: "Period end",
        bankRules: "Bank rules",
        statements: "Financial statements",
        proofs: "Proofs",
        statementLayouts: "Statement layouts",
        collections: "Collections",
        reconciliation: "Bank reconciliation",
        supplierInvoices: "Supplier invoices",
        taxCodes: "Tax codes",
      },
      paymentRuns: {
        alreadyPosted: "This run has already posted.",
        approvalRaised: "An approval request is open for this run.",
        back: "Back to payment runs",
        currencyRequired: "Enter a three-letter currency code.",
        debtorBic: "Debtor BIC",
        debtorIban: "Debtor IBAN",
        debtorName: "Debtor account name",
        debtorRequired: "The payment file needs the debtor account name, IBAN and BIC.",
        detailSubtitle: "Payment date {{date}}",
        discount: "Discount",
        dueCutoffHint: "Invoices inside their discount window are selected regardless.",
        dueDate: "Due",
        dueOnOrBefore: "Due on or before",
        export: "Export payment file",
        exportHint:
          "ISO 20022 pain.001. The writer is deterministic, so regenerating reproduces the file byte for byte.",
        exportSection: "Bank payment file",
        fileHash: "Exported file hash",
        gross: "Gross",
        invoice: "Invoice",
        linesSection: "Selected invoices",
        net: "Net",
        noPayables: "No posted payables to choose from.",
        notExported: "Not exported yet",
        notFound: "This payment run could not be loaded.",
        paymentDate: "Payment date",
        pickForbidden: "Hand-picking needs permission to read supplier invoices.",
        pickHint: "Leave everything unticked to propose every eligible open item.",
        pickSection: "Narrow to a hand-picked set",
        post: "Post the run",
        postBlocked: "Posting is refused until the run is approved.",
        propose: "Propose run",
        proposeSection: "Propose a run",
        selectedCount: "{{count}} invoices hand-picked",
        status: {
          approved: "Approved",
          cancelled: "Cancelled",
          draft: "Draft",
          pending_approval: "Pending approval",
          posted: "Posted",
          proposed: "Proposed",
        },
        submissionReason: "Note",
        submit: "Submit run",
        submitHint:
          "When no rule requires a step the engine approves it immediately — check the status afterwards.",
        submitSection: "Submit for approval",
        subtitle: "Propose a run, review it, then submit it for approval.",
        supplier: "Supplier",
        title: "Payment runs",
        total: "Total",
      },
      collections: {
        title: "Collections",
        subtitle:
          "How overdue invoices are chased, and the statement every customer with an open balance receives.",
        ladderTitle: "Escalation ladder",
        ladderHint:
          "Each rung says how overdue an invoice must be, and how much must be open, before that letter goes out. Removing a rung deactivates it: a letter sent last year names a level that must stay explicable.",
        noLevels: "No level is configured, so nothing is chased.",
        level: "Level",
        name: "Name",
        daysOverdue: "Days overdue",
        minOpen: "Least open",
        template: "Letter",
        active: "Active",
        addLevel: "Add a level",
        removeLevel: "Remove level",
        saveLadder: "Save the ladder",
        ladderSaved:
          "The ladder is now these {{count}} levels. Anything removed was deactivated, not deleted.",
        ladderError: "The ladder could not be saved.",
        notEscalating:
          "A later level waits no longer than an earlier one, so it would never be reached.",
        sweepTitle: "Run the sweep",
        sweepHint:
          "The same sweep the scheduler runs nightly. An invoice already dunned at a level is not dunned again, so running it twice on one day sends nothing the second time.",
        sweep: "Queue the sweep",
        sweepQueued:
          "The sweep for {{date}} is queued. An invoice already dunned at a level is not dunned again, so a rerun sends nothing.",
        sweepError: "The sweep could not be queued.",
        asOf: "Overdue as of",
        currency: "Currency",
        statementsTitle: "Customer statements",
        statementsHint:
          "Rendering and sending happen in the background, so this reports what the run has done so far rather than claiming the statements are out.",
        sendStatements: "Send the statements",
        statementError: "The statement run could not start.",
        runQueued: "The run is queued.",
        runCounts: "{{sent}} of {{parties}} sent · {{failed}} failed",
      },
      dunningTemplate: {
        dunning_level_1: "First reminder",
        dunning_level_2: "Second reminder",
        dunning_level_3: "Final notice",
      },
      statementRunStatus: {
        pending: "Queued",
        running: "Sending",
        completed: "Done",
        failed: "Failed",
      },
      proofs: {
        title: "Proofs",
        subtitle:
          "Reports that derive their figures twice and refuse to answer when the two disagree. What you see here has already been checked.",
        period: "Period",
        pickPeriod: "Pick a period…",
        pickFirst: "Pick a period.",
        taxTab: "Tax return",
        assetsTab: "Asset register",
        grniTab: "Received, not invoiced",
        taxTitle: "Tax return",
        taxHint:
          "Computed from posted journals, never from source documents, and company-wide: a branch is a site of one legal entity with one registration.",
        taxCode: "Code",
        outputTax: "Charged on sales",
        inputTax: "Recoverable",
        netTax: "Net",
        total: "Total",
        tieOut:
          "Each control account's movement was derived three ways and agreed. {{untagged}} posted line(s) hit a tax account with no code on them.",
        assetsTitle: "Fixed-asset register",
        assetsHint:
          "Net book value as of the period end. An asset is judged present by its disposal date, so a register run for March still shows an asset sold in June.",
        class: "Class",
        assetCount: "Assets",
        cost: "Cost",
        depreciation: "Depreciated",
        netBookValue: "Net book value",
        tiesOut: "Ties to the control accounts",
        registerVsLedger: "Subledger and ledger were derived separately and agree.",
        grniTitle: "Received, not invoiced",
        grniHint:
          "The clearing account against the accrual still carried by receipt lines. The two sides come from different places on purpose — a tie-out that computed one from the other would prove nothing.",
        currency: "Currency",
        asOf: "As of",
        subledger: "From the receipts",
        ledger: "From the ledger",
        accrued: "Accrued by receipts",
        cleared: "Cleared by invoices",
        agrees: "The two sides agree",
        disagrees: "The two sides disagree",
        nothingOpen: "Nothing is received and uninvoiced.",
        receiptLine: "Receipt line",
        received: "Received",
        invoiced: "Invoiced",
        open: "Still open",
        withheld:
          "This report was withheld because its two derivations disagree. Publishing a plausible figure here is how a broken set of books survives to year end.",
        error: "The report could not be read.",
      },
      statements: {
        title: "Financial statements",
        subtitle:
          "Grouped by the layout that was in force at the period end, so an earlier period still reads the way it did.",
        statement: "Statement",
        period: "Period",
        pickPeriod: "Pick a period…",
        pickFirst: "Pick a period to read a statement.",
        layout: "Layout",
        inForce: "Whatever was in force",
        comparative: "Compare with",
        priorYear: "The year before",
        noComparative: "Nothing",
        line: "Line",
        periodColumn: "This period",
        yearToDate: "Year to date",
        closing: "Closing balance",
        priorPeriod: "Same period before",
        priorYtd: "Before, to date",
        accountCount: "{{count}} account(s)",
        live: "Computed now",
        fromSnapshot: "As filed at close",
        assertionsTitle: "What was proved",
        assertionsHint:
          "Both sides are published so the subtraction can be redone. A statement whose proof fails is not returned at all.",
        generatedAt: "Produced {{time}}",
        blockedTitle: "This layout cannot produce it yet",
        blockedHint:
          "Every account must land on exactly one line. Until then the statement would silently omit a balance, so it is not produced at all.",
        error: "The statement could not be read.",
      },
      assertion: {
        trial_balance_unbalanced: "Debits equal credits",
        balance_sheet_unbalanced: "Assets equal liabilities plus equity",
        cash_flow_does_not_reconcile: "The movement reconciles to cash",
      },
      statementLayouts: {
        title: "Statement layouts",
        subtitle:
          "How a statement is grouped. Saving makes a new version; the one a filed statement cited stays exactly as it was.",
        versions: "Layouts and versions",
        empty: "No layout is defined.",
        pick: "Pick a layout to see how it groups.",
        new: "New layout",
        newVersion: "New version from this",
        saveVersion: "Save as a new version",
        editorHint:
          "Saving creates a version. Reusing a code makes the next version of that layout and leaves the previous one exactly as it was.",
        version: "v{{version}}",
        from: "from {{date}}",
        code: "Code",
        type: "Statement",
        name: "Name",
        notes: "Notes",
        validFrom: "In force from",
        provenance: "Code, type and version are what a filed statement cites, so they cannot move.",
        linesTitle: "Lines",
        line: "Line",
        lineCode: "Line code",
        label: "Label",
        ordinal: "Order",
        kind: "Value from",
        parent: "Rolls into",
        noParent: "Nothing (top level)",
        activity: "Activity",
        noActivity: "No activity",
        selects: "Selects",
        signFlip: "Shown positive",
        signFlipLabel: "Show as positive",
        ruleKind: "Rule",
        addRule: "Add a selection rule",
        removeRule: "Remove rule",
        addLine: "Add line",
        removeLine: "Remove line",
        codeFrom: "From code",
        codeTo: "To code",
        accountType: "Account type",
        accountTag: "Account tag",
        computedLine:
          "This line is computed, so it maps no accounts — that is what keeps a movement from being counted twice.",
        checkTitle: "Can it produce a statement?",
        checkHint:
          "Every postable account — and every retired account still carrying movement — must land on exactly one line. Every issue is listed at once.",
        check: "Check this layout",
        checkError: "The check could not run.",
        usable: "This layout can produce a statement.",
        blocked: "This layout cannot produce a statement yet.",
        counts:
          "{{mapped}} of {{eligible}} accounts mapped · {{unmapped}} unmapped · {{untagged}} untagged",
        saveError: "The layout could not be saved.",
        createError: "The layout could not be saved. Check that every line has a unique code.",
      },
      statementType: {
        profit_and_loss: "Profit and loss",
        balance_sheet: "Balance sheet",
        cash_flow: "Cash flow",
      },
      statementLineKind: {
        detail: "The accounts it selects",
        subtotal: "Its children",
        current_year_earnings: "Undistributed profit",
        net_income: "Net income",
        cash: "Cash population",
        unclassified: "Reconciling remainder",
      },
      statementRuleKind: {
        account_code_range: "Code range",
        account_type: "Account type",
        account_tag: "Account tag",
      },
      cashFlowActivity: { operating: "Operating", investing: "Investing", financing: "Financing" },
      statementValidation: {
        account_unmapped: "An account lands on no line",
        account_multiply_mapped: "An account lands on more than one line",
        account_wrong_statement: "An account belongs to a different statement",
        required_line_missing: "A required line is missing",
        line_duplicated: "A line appears twice",
        line_kind_not_allowed: "This statement does not have that kind of line",
        line_cannot_map_accounts: "A computed line carries account rules",
        activity_line_missing: "An activity has no line",
        activity_line_duplicated: "An activity has more than one line",
        activity_missing: "An account carries no activity tag",
        subtotal_without_children: "A subtotal has nothing rolling into it",
        parent_cycle: "Lines roll into each other in a circle",
        parent_not_subtotal: "A line rolls into something that is not a subtotal",
        rule_invalid: "A selection rule is not usable",
        layout_empty: "The layout has no lines",
        cash_account_tagged: "A cash account also carries an activity tag",
      },
      bankRules: {
        title: "Bank rules",
        subtitle: "What a statement line does before anyone looks at it.",
        postingTitle: "Auto-posting rules",
        postingHint:
          "These run before matching, in sequence order, and the first match wins. They are for the lines no document was ever raised for — charges, interest, commission.",
        noPosting: "No auto-posting rule is defined.",
        newPosting: "New auto-posting rule",
        editPosting: "Edit auto-posting rule",
        postingFormHint:
          "Rules are tried in order and the first match wins. The amount bounds are absolute values, so one rule covers a fee whether it is charged or refunded.",
        referenceTitle: "Reference rules",
        referenceHint:
          "These pull a document reference out of free-text narrative so matching has something to match on. They decide what a line is matched against, never what it posts to.",
        noReference: "No reference rule is defined.",
        newReference: "New reference rule",
        editReference: "Edit reference rule",
        referenceFormHint:
          "The lowest priority runs first. The capture group is the part of the match that is the reference; 0 means the whole match.",
        sequence: "Order",
        priority: "Priority",
        rule: "Rule",
        name: "Name",
        matches: "Matches",
        postsTo: "Posts to",
        pattern: "Pattern",
        referencePattern: "Reference pattern",
        counterparty: "Counterparty pattern",
        target: "Points at",
        group: "Capture group",
        captureGroup: "Group {{group}}",
        direction: "Direction",
        minAmount: "Least",
        maxAmount: "Most",
        bankAccount: "Bank account",
        everyAccount: "Every bank account",
        otherAccount: "Another bank account",
        noBankAccounts: "No bank account found.",
        pickAccount: "Account…",
        noAccounts: "No account matches.",
        taxCode: "Tax code",
        noTax: "No tax",
        noTaxCodes: "No tax code found.",
        active: "Active",
        inactive: "Inactive",
        badPattern:
          "This pattern does not compile. A rule that does not compile is skipped silently, so it is refused here instead.",
        unconstrained:
          "Name a counterparty, a reference, or an amount bound. A rule that constrains nothing would post every line on the statement.",
        saveError: "The rule could not be saved.",
        deleteError: "The rule could not be deleted.",
      },
      bankDirection: { inbound: "Money in", outbound: "Money out", any: "Either way" },
      bankDocumentType: {
        customer_receipt: "Customer receipt",
        supplier_payment: "Supplier payment",
        customer_invoice: "Customer invoice",
        supplier_invoice: "Supplier invoice",
        gateway_payout: "Gateway payout",
        posting_rule: "Auto-posting rule",
      },
      periodEnd: {
        title: "Period end",
        subtitle:
          "Draft what recurs, revalue what is held in another currency, and close the year.",
        period: "Period",
        pickPeriod: "Pick a period…",
        recurringTitle: "Recurring journals",
        recurringHint:
          "Templates draft an entry on their day each month. Drafting twice is not possible: a second run reports what was already there.",
        noTemplates: "No recurring template is defined.",
        template: "Template",
        schedule: "Runs on",
        dayOfMonth: "day {{day}}",
        window: "Window",
        reversal: "Auto-reverse",
        reverses: "Reverses next period",
        materialize: "Draft what is due",
        materialized: "{{created}} drafted, {{existing}} already there.",
        materializeError: "The drafts could not be created.",
        newTemplate: "New recurring template",
        editTemplate: "Edit recurring template",
        templateHint:
          "Positive is a debit. The lines must sum to zero, and saving replaces them whole.",
        code: "Code",
        currency: "Currency",
        name: "Name",
        description: "Description",
        fixedAtCreation: "Fixed once the template exists.",
        dayLabel: "Day of month",
        dayHint: "Clamped to the last day of a shorter month.",
        startsOn: "Starts on",
        endsOn: "Ends on",
        endsOnHint: "Leave empty for a template that does not end.",
        autoReverse: "Reverse in the following period",
        active: "Active",
        inactive: "Inactive",
        lines: "Lines",
        balance: "Balance",
        account: "Account",
        pickAccount: "Account…",
        noAccounts: "No account matches.",
        amount: "Amount",
        lineDescription: "Line description",
        addLine: "Add line",
        removeLine: "Remove line",
        unbalanced:
          "The lines are off by {{amount}}. A template that does not balance drafts an entry nobody can post.",
        saveError: "The template could not be saved.",
        fxTitle: "Foreign-currency revaluation",
        fxHint:
          "Restates open receivables, payables and bank balances at the closing rate. Running again reverses the previous run and writes a new one; nothing is overwritten.",
        noRuns: "No revaluation has been run yet.",
        runNo: "Run {{no}}",
        lineCount: "{{count}} position(s)",
        revalue: "Revalue this period",
        revalued: "Revalued",
        revalueDone: "Run {{no}} recorded {{count}} position(s).",
        revalueError:
          "The revaluation could not run. It needs a closing rate for every open currency.",
        scope: "Position",
        foreign: "Held",
        rate: "Closing rate",
        booked: "Booked",
        delta: "Gain / loss",
        yearTitle: "Year-end close",
        yearHint:
          "Sweeps the year's income and expense balances into retained earnings. The server chooses the closing period and the equity account.",
        closeYear: "Close {{code}}",
        yearClosed: "Swept {{count}} account(s) into retained earnings.",
        yearCloseError: "The year-end close could not run.",
      },
      fxRunStatus: { completed: "Live", reversed: "Superseded" },
      fxScope: { ap: "Payable", ar: "Receivable", bank: "Bank balance" },
      periodClose: {
        advisoryTask: "Advisory",
        automatedNotSignable:
          "An automated task cannot be signed off. It is answered by its check.",
        blocked: "{{count}} tasks are still in the way.",
        blockingCodes: "Blocking: {{codes}}",
        blockingTask: "Blocking",
        closeable: "Nothing stands in the way.",
        evidenceCount: "{{shown}} rows shown of {{total}} found",
        executeYearEnd: "Sweep the year into retained earnings",
        fiscalYear: "Fiscal year",
        note: "Note",
        period: "Fiscal period",
        periodStatus: {
          closed: "closed",
          closing: "closing",
          open: "open",
        },
        reopen: "Reopen the period",
        reopenHint: "A controlled reason code — the audit trail never accumulates free-form prose.",
        reopenReason: "Reopen reason",
        reopenReasonOption: {
          authorized_reopen: "Authorized reopen",
          controlled_correction: "Controlled correction",
          migration: "Migration",
        },
        retainedEarnings: "Moved to retained earnings",
        runChecks: "Run every automated check",
        signOff: "Sign off",
        subtitle: "The checklist, its evidence, and the one step the period may take next.",
        taskKind: {
          automated: "automated",
          manual: "manual",
        },
        taskStatus: {
          failed: "Failed",
          open: "Open",
          passed: "Passed",
          signed_off: "Signed off",
          skipped: "Skipped — still blocking",
          waived: "Waived",
        },
        tasksSection: "Checklist",
        title: "Period close",
        transition: "Move to {{status}}",
        waive: "Waive with a reason",
        waiverReasonRequired:
          "A waiver demands a reason in writing — without one nobody can find out why the close was allowed.",
        yearEndApprovalRequired:
          "The close was routed for approval. Nothing posts until the decision lands.",
        yearEndCounts: "{{accounts}} accounts closed · {{openings}} opening balances seeded",
        yearEndHint:
          "One journal closes every income and expense account into retained earnings. The trial balance is proved to net to zero before and after; the sweep happens once.",
        yearEndSection: "Year-end close",
        yearNotClosed: "This year has not been closed.",
      },
      permissions: {
        code: "403 · ACCESS CONTROL",
        title: "Finance access restricted",
      },
      receipts: {
        allocate: "Allocate",
        allocateTo: "Allocate to {{invoice}}",
        allocatedAmount: "Allocated",
        allocationHint:
          "This is the whole plan, not a change to it. Anything you leave unallocated lands on the customer's account.",
        allocationInvalid: "Enter allocation amounts with at most four decimals.",
        allocationSection: "Allocate against open invoices",
        amount: "Amount received",
        amountInvalid: "Enter the amount received, with at most four decimals.",
        back: "Back",
        bankAccount: "Bank account",
        bankRequired: "Choose the bank account the money reached.",
        create: "Record receipt",
        createSubtitle: "Recording the money creates a draft. It posts when you allocate it.",
        createTitle: "Record a customer receipt",
        customer: "Customer",
        customerRequired: "Choose the customer.",
        detailsSection: "Receipt",
        draftTitle: "Draft receipt",
        dueDate: "Due",
        enteredTotal: "Allocating",
        exceedsInvoice: "Allocating {{amount}} to {{invoice}} exceeds its open amount.",
        exceedsReceipt: "The allocations add up to more than the money received.",
        invoice: "Invoice",
        method: "Method",
        methodOption: {
          bank_transfer: "Bank transfer",
          card: "Card",
          cash: "Cash",
          cheque: "Cheque",
          online_gateway: "Online gateway",
          other: "Other",
        },
        noOpenInvoices: "This customer has no open invoices.",
        notFound: "This receipt could not be loaded.",
        onAccount: "On account",
        openAmount: "Open",
        overpaymentNotice:
          "The remainder is an overpayment on the customer's account — a legitimate outcome, not an error.",
        planApplied: "Applied",
        planOnAccount: "on account",
        planRemaining: "leaves {{amount}} open",
        planSettled: "settles the invoice",
        planTitle: "Stored allocation",
        receivedOn: "Received on",
        reference: "Reference",
        remainder: "On account after this",
        saveAllocation: "Save allocation",
        settleInFull: "Settle in full",
      },
      reconciliation: {
        addPart: "Add part",
        amount: "Amount",
        bankAccount: "Bank account",
        bookingDate: "Booked",
        columns: {
          amount: "Amount",
          bookingDate: "Booked",
          counterparty: "Counterparty",
          reference: "Reference",
          status: "Status",
          unmatched: "Unmatched",
        },
        directionMismatch: "Every part must move money the same way the line does.",
        documentType: {
          customer_invoice: "Customer invoice",
          customer_receipt: "Customer receipt",
          gateway_payout: "Gateway payout",
          posting_rule: "Posting rule",
          supplier_invoice: "Supplier invoice",
          supplier_payment: "Supplier payment",
        },
        empty: "No statement lines match these filters.",
        exclude: "Exclude from the worklist",
        excludedNotice: "Excluded: {{reason}}",
        exclusionReason: "Exclusion reason",
        filterBankAccount: "Bank account",
        filterFrom: "Booked from",
        filterStatus: "Status",
        filterTo: "Booked to",
        lineSection: "Statement line",
        matchSection: "Match and split",
        matched: "Matched",
        noMatches: "Nothing is matched to this line yet.",
        noSuggestions: "No candidates.",
        partAmount: "Amount",
        partAmountInvalid: "Part {{part}} needs a non-zero amount.",
        partDocument: "Document",
        partDocumentRequired: "Part {{part}} needs a document.",
        partType: "Document type",
        partsRequired: "Add at least one part.",
        pass: "pass {{pass}}",
        post: "Post to ledger",
        refreshSuggestions: "Re-run the match engine",
        removePart: "Remove part {{part}}",
        saveSplit: "Replace the split",
        split: "Split",
        splitBalances: "The split adds up to the line exactly.",
        splitDoesNotSum:
          "The parts must add up to the line exactly. They are out by {{difference}}.",
        splitOutBy: "Out by {{difference}}",
        status: "Status",
        statusOption: {
          excluded: "Excluded",
          imported: "Imported",
          matched: "Matched",
          posted: "Posted",
          suggested: "Suggested",
        },
        subtitle: "Statement movements, what they settle, and what still has to be explained.",
        suggestions: "Candidates",
        suggestionsHint:
          "Strongest evidence first. A suggestion is never a decision — nothing posts until a match is accepted.",
        title: "Bank reconciliation",
        unmatched: "Unmatched",
        useSuggestion: "Add to split",
      },
      supplierInvoices: {
        addLine: "Add line",
        alreadyPosted: "This invoice has already posted.",
        amountPaid: "Paid",
        backToList: "Back to supplier invoices",
        capture: "Capture invoice",
        captureAction: "Capture and match",
        captureSubtitle:
          "Tax is calculated and the three-way match runs in the same transaction as the capture.",
        captureTitle: "Capture supplier invoice",
        codingExpense: "Expense account",
        codingHint: "A line is one or the other, never both.",
        codingPo: "Purchase-order line",
        columns: {
          documentNo: "Document",
          dueDate: "Due",
          grandTotal: "Total",
          invoiceDate: "Invoice date",
          openFindings: "Open findings",
          status: "Status",
          supplier: "Supplier",
          supplierNumber: "Supplier reference",
        },
        detailSubtitle: "Supplier reference {{reference}} · {{supplier}}",
        discountDays: "Discount window (days)",
        discountDaysInvalid: "The discount window is 0 to 365 days.",
        discountInvalid: "Enter the early-settlement discount as a percentage.",
        discountPercent: "Early settlement discount %",
        dueBeforeInvoice: "The due date cannot precede the invoice date.",
        dueDate: "Due date",
        empty: "No supplier invoices match these filters.",
        filterStatus: "Status",
        filterSupplier: "Supplier",
        findingClosed: "Closed.",
        findingKind: {
          missing_receipt: "No goods receipt",
          price_tolerance: "Price outside tolerance",
          qty_over_receipt: "Invoiced more than received",
        },
        findingLine: "Line {{line}} · {{description}}",
        findingReason: "Reason",
        findingStatus: {
          open: "Open",
          overridden: "Overridden",
          resolved: "Resolved",
        },
        findingValues: "Expected {{expected}} · invoiced {{actual}}",
        findingsSection: "Match findings",
        grandTotal: "Total",
        grnLineHint: "Leaving this empty raises a missing_receipt finding.",
        headerSection: "Invoice",
        invoiceDate: "Invoice date",
        lineAccountRequired: "Line {{line}} needs an expense account.",
        lineCoding: "Coding",
        lineDescription: "Description",
        lineDescriptionRequired: "Line {{line}} needs a description.",
        lineExpenseAccount: "Expense account",
        lineGrnLine: "Goods receipt line",
        lineHeading: "Line {{line}}",
        lineNet: "Net",
        linePoLine: "Purchase-order line",
        linePoLineRequired: "Line {{line}} needs the purchase-order line it invoices.",
        linePriceInvalid: "Line {{line}} needs a unit price.",
        lineQuantity: "Quantity",
        lineQuantityInvalid: "Line {{line}} needs a non-zero quantity.",
        lineTax: "Tax",
        lineTaxCode: "Tax code",
        lineTotal: "Total",
        lineUnitPrice: "Unit price",
        linesSection: "Lines",
        netPreview: "Net of entered lines: {{amount}} {{currency}}",
        noFindings: "The match found nothing to answer for.",
        noPurchaseOrder: "No purchase order",
        noReceipt: "No receipt (raises a finding)",
        notFound: "This supplier invoice could not be loaded.",
        numberRequired:
          "Enter the supplier's own invoice number — it is what duplicates are checked against.",
        override: "Override — accept the variance",
        overridePending:
          "This override is with the approval workflow. The finding stays open until the decision lands.",
        post: "Post to ledger",
        postBlocked: "Posting is refused while a match finding is open. Close every finding first.",
        postedAt: "Posted",
        priceInvalid: "Prices carry at most six decimals.",
        purchaseOrder: "Purchase order",
        purchaseOrderHint: "Pick one to code lines against its lines and receipts.",
        quantityInvalid: "Quantities carry at most six decimals and cannot be zero.",
        reasonRequired: "A finding cannot be closed without a written reason.",
        rematch: "Re-run match",
        removeLine: "Remove line {{line}}",
        resolve: "Resolve — the variance went away",
        status: {
          discrepancy: "Discrepancy",
          draft: "Draft",
          matched: "Matched",
          paid: "Paid",
          posted: "Posted",
          void: "Void",
        },
        subtitle: "Captured supplier invoices with their three-way-match verdict.",
        subtotal: "Net",
        supplier: "Supplier",
        supplierNumber: "Supplier invoice number",
        supplierNumberHint: "Guarded per supplier, case-insensitively and ignoring punctuation.",
        supplierRequired: "Choose the supplier.",
        taxCodeHint: "Pin a code, or leave it for the tax engine to determine.",
        taxDetermined: "Determined by the engine",
        taxPercent: "{{percent}}%",
        taxTotal: "Tax payable to supplier",
        title: "Supplier invoices",
      },
      taxCodes: {
        activeLabel: "Available for new documents",
        addRate: "Add rate",
        addRateHint:
          "A rate change is a new row, never an edit. Overlapping validity for one code is refused.",
        code: "Code",
        codeFrozen: "Frozen after creation — documents are filed under it.",
        codeNameRequired: "A tax code needs a code and a name.",
        columns: {
          active: "Active",
          code: "Code",
          currentRate: "Rate today",
          jurisdiction: "Jurisdiction",
          kind: "Kind",
          name: "Name",
        },
        create: "Create tax code",
        createTitle: "New tax code",
        editTitle: "Edit {{code}}",
        empty: "No tax codes match these filters.",
        filterActive: "Status",
        filterJurisdiction: "Jurisdiction",
        inForce: "In force today",
        inputAccount: "Input tax account",
        inputAccountHint: "Debited by purchase-side postings.",
        jurisdiction: "Jurisdiction",
        jurisdictionHint: "ISO country, optionally with a subdivision (SA, SA-01).",
        jurisdictionRequired: "Name the jurisdiction.",
        kind: {
          exempt: "Exempt",
          reverse_charge: "Reverse charge",
          standard: "Standard",
          zero_rated: "Zero-rated",
        },
        kindHint: "Decides how many ledger legs the code posts. Frozen after creation.",
        kindLabel: "Kind",
        name: "Name",
        newCode: "New tax code",
        noAccount: "Not mapped",
        noRates: "No rates yet.",
        openEnded: "open-ended",
        openingRateHint:
          "Opening rate. The code and its rate are created in one transaction, so a code is never persisted unpriced.",
        outputAccount: "Output tax account",
        outputAccountHint: "Credited by sales-side postings.",
        percent: "Percent",
        percentInvalid:
          'Enter a percentage between 0 and 100 with at most six decimals. "15" means 15%.',
        ratesAfterCreate: "Rate history appears once the code exists.",
        ratesTitle: "Rate history",
        reverseChargeAccounts:
          "A reverse-charge code posts both legs, so it needs both control accounts.",
        save: "Save tax code",
        subtitle:
          "Company tax codes, their control accounts and their effective-dated rate history.",
        title: "Tax codes",
        validFrom: "Effective from",
        validFromRequired: "A rate needs the date it takes effect.",
        validRangeInvalid: "The end date cannot precede the start date.",
        validTo: "Effective to",
        validToHint: "Leave empty for an open-ended rate.",
        zeroRateRequired: "A zero-rated or exempt code must carry a zero rate.",
      },
    },
  },
  ar: {
    finance: {
      aging: {
        apForbidden: "لا تملك صلاحية قراءة تقرير أعمار الذمم الدائنة.",
        apTotals: "الذمم الدائنة حسب الشريحة",
        arForbidden: "لا تملك صلاحية قراءة تقرير أعمار الذمم المدينة.",
        arTotals: "الذمم المدينة حسب الشريحة",
        asOf: "كما في تاريخ",
        bucket: {
          "1_30": "1–30 يومًا",
          "31_60": "31–60 يومًا",
          "61_90": "61–90 يومًا",
          "90_plus": "أكثر من 90 يومًا",
          current: "غير مستحق",
        },
        currencyHint: "إلزامية. لا يجوز أن تجمع الشريحة الواحدة أكثر من عملة.",
        customer: "العميل",
        doesNotTieOut:
          "هذا التقرير لا يطابق حساب مراقبة الذمم المدينة. الفرق {{difference}} — أحد الطرفين خاطئ، فلا تعتمد هذه الأرقام.",
        run: "تشغيل التقرير",
        subtitle:
          "الذمم المدينة والدائنة موزعة على شرائح من تاريخ الاستحقاق، بعملة واحدة في كل مرة.",
        supplier: "المورد",
        tabAP: "الذمم الدائنة",
        tabAR: "الذمم المدينة",
        tiesOut: "هذا التقرير يطابق حساب مراقبة الذمم المدينة عند {{balance}}.",
        title: "أعمار الذمم",
        total: "الإجمالي",
      },
      assetClasses: {
        accountsMustDiffer: "يجب أن تختلف حسابات المراقبة الأربعة عن بعضها.",
        accountsRequired:
          "الحسابات الرقابية الأربعة إلزامية — الحساب غير المربوط يرحّل إلى مكان عشوائي.",
        accumulatedAccount: "حساب مجمّع الإهلاك",
        activeLabel: "نشط",
        code: "الرمز",
        codeFrozen: "غير قابل للتعديل.",
        codeNameRequired: "فئة الأصول تحتاج رمزًا واسمًا.",
        costAccount: "حساب التكلفة",
        create: "إنشاء الفئة",
        createTitle: "فئة أصول جديدة",
        defaultFactor: "معامل التناقص الافتراضي",
        defaultLife: "العمر الإنتاجي الافتراضي (أشهر)",
        defaultMethod: "الطريقة الافتراضية",
        edit: "تعديل",
        editTitle: "تعديل {{code}}",
        empty: "لا توجد فئات أصول بعد.",
        expenseAccount: "حساب مصروف الإهلاك",
        gainLossAccount: "حساب الربح أو الخسارة من الاستبعاد",
        lifeMonths: "{{months}} شهرًا",
        listSection: "الفئات",
        name: "الاسم",
        new: "فئة أصول جديدة",
        save: "حفظ الفئة",
        subtitle:
          "الإعدادات الافتراضية للإهلاك التي ترثها الأصول الجديدة، والحسابات الرقابية الأربعة التي يرحّل عبرها كل أصل في الفئة.",
        switchDefault: "يتحول القسط المتناقص إلى القسط الثابت متى صار الأخير أعلى",
        title: "فئات الأصول",
      },
      bankAccounts: {
        accountNumber: "رقم الحساب",
        accountsMustDiffer:
          "يجب أن يختلف حساب الأستاذ عن حساب المقاصة — وإلا فإن التسوية لا تثبت شيئًا.",
        accountsRequired: "اختر حساب الأستاذ العام وحساب المقاصة تحت التحصيل معًا.",
        activeLabel: "نشط",
        bic: "رمز BIC",
        clearingAccount: "حساب المقاصة تحت التحصيل",
        clearingAccountHint: "حيث يبقى المال إلى أن يحركه البنك فعليًا.",
        code: "الرمز",
        codeFrozen: "غير قابل للتعديل.",
        codeNameRequired: "الحساب البنكي يحتاج رمزًا واسمًا.",
        columns: {
          active: "نشط",
          code: "الرمز",
          iban: "الآيبان",
          name: "الاسم",
        },
        create: "تسجيل الحساب",
        createTitle: "حساب بنكي جديد",
        currencyFrozen: "غير قابلة للتعديل.",
        editTitle: "تعديل {{code}}",
        empty: "لم يُسجَّل أي حساب بنكي بعد.",
        filterActive: "الحالة",
        glAccount: "حساب الأستاذ",
        glAccountHint: "حيث يقيم الرصيد المُسوّى.",
        iban: "الآيبان",
        name: "الاسم",
        new: "حساب بنكي جديد",
        save: "حفظ الحساب",
        subtitle: "حسابات الشركة نفسها — التي تصل منها كشوف الحساب.",
        title: "الحسابات البنكية",
      },
      bankStatements: {
        accountRequired: "اختر الحساب البنكي.",
        bankAccount: "الحساب البنكي",
        columns: {
          bankAccount: "الحساب البنكي",
          closing: "الرصيد الختامي",
          duplicates: "المكرر",
          fileName: "الملف",
          imported: "المستورد",
          period: "الفترة",
          status: "الحالة",
        },
        empty: "لم يُستورد أي كشف بعد.",
        file: "ملف الكشف",
        fileRequired: "اختر ملف الكشف.",
        fileTooLarge: "أقصى حجم لملف الكشف 16 ميبي بايت.",
        fileUnreadable: "تعذرت قراءة هذا الملف.",
        filterBankAccount: "التصفية بالحساب",
        format: "الصيغة",
        formatHint:
          "صيغتا CAMT.053 و OFX تصفان نفسيهما؛ أما CSV فموثوقيتها بقدر ملف التنسيق خلفها.",
        formatOption: {
          camt053: "CAMT.053",
          csv: "CSV",
          ofx: "OFX",
        },
        gapDetected:
          "الرصيد الافتتاحي لا يكمل الرصيد الختامي المخزن لهذا الحساب. هناك حركات مفقودة.",
        gapDetectedExpected:
          "الرصيد الافتتاحي لا يكمل الرصيد الختامي المخزن لهذا الحساب البالغ {{expected}}. هناك حركات مفقودة.",
        import: "استيراد الكشف",
        importSection: "استيراد كشف حساب",
        importSummary: "استُوردت {{imported}} حركة، و{{duplicates}} كانت مسجلة من قبل.",
        profile: "تنسيق CSV",
        profileDefault: "التنسيق النشط للحساب",
        statusOption: {
          gap_detected: "فجوة مكتشفة",
          imported: "مستورد",
        },
        subtitle: "ملفات كشوف الحساب المستوردة واتصالها ببعضها وما أضافته.",
        title: "كشوف الحسابات البنكية",
      },
      common: {
        active: "نشط",
        all: "الكل",
        branchRequired: "اختر الفرع الفاعل قبل إنشاء مستند تشغيلي.",
        choose: "اختر…",
        currency: "العملة",
        fxRate: "سعر الصرف",
        fxRateHint: "عدد وحدات العملة الأساسية مقابل وحدة عملة المستند.",
        fxRateInvalid: "أدخل سعر صرف موجبًا.",
        inactive: "غير نشط",
      },
      customerInvoices: {
        addLine: "إضافة بند",
        allocatedTotal: "المخصص",
        backToList: "العودة إلى فواتير العملاء",
        columns: {
          customer: "العميل",
          dueDate: "الاستحقاق",
          grossTotal: "الإجمالي",
          invoiceNo: "الرقم",
          issueDate: "تاريخ الإصدار",
          openAmount: "المتبقي",
          status: "الحالة",
        },
        create: "فاتورة جديدة",
        createCreditNote: "إنشاء إشعار دائن مسودة",
        createDraft: "إنشاء فاتورة مسودة",
        createSubtitle: "ينشئ مسودة. يُخصص رقم السلسلة النظامية عند الاعتماد.",
        createTitle: "فاتورة عميل جديدة",
        creditIssueDate: "تاريخ الإصدار",
        creditLinesRequired: "أضف بندًا واحدًا على الأقل إلى الإشعار الدائن.",
        creditNoteDraft: "إشعار دائن مسودة",
        creditNoteHint:
          "الكمية المدينة وحدها هي مدخلك. الوصف والسعر وحساب الإيراد ولقطة الضريبة تُنسخ من بند الفاتورة.",
        creditNoteSection: "إشعار دائن",
        creditNoteStatus: {
          draft: "مسودة",
          posted: "مرحّل",
          void: "ملغى",
        },
        creditQuantity: "كمية الإشعار",
        creditQuantityInvalid: "البند {{line}}: أدخل كمية غير صفرية بستة أرقام عشرية كحد أقصى.",
        creditQuantityLine: "إشعار البند {{line}}",
        creditReason: "السبب",
        creditReasonRequired: "الإشعار الدائن يحتاج سببًا مكتوبًا.",
        creditedTotal: "المُشعر دائنًا",
        customer: "العميل",
        customerRequired: "اختر العميل.",
        draftTitle: "فاتورة مسودة",
        dueBeforeIssue: "لا يجوز أن يسبق تاريخ الاستحقاق تاريخ الإصدار.",
        dueDate: "تاريخ الاستحقاق",
        dueDateHint: "اتركه فارغًا ليُشتق من شروط سداد العميل.",
        empty: "لا توجد فواتير عملاء مطابقة لهذه المرشحات.",
        filterCustomer: "العميل",
        filterStatus: "الحالة",
        finalize: "إصدار وترحيل",
        finalizeCreditNote: "إصدار وترحيل الإشعار الدائن",
        grossTotal: "الإجمالي",
        headerSection: "الفاتورة",
        immutable: "الفاتورة المرحّلة غير قابلة للتعديل. التصحيح الوحيد هو إشعار دائن.",
        issueDate: "تاريخ الإصدار",
        lineDescription: "الوصف",
        lineDescriptionRequired: "البند {{line}} يحتاج وصفًا.",
        lineHeading: "البند {{line}}",
        lineNet: "الصافي",
        linePriceInvalid: "البند {{line}} يحتاج سعر وحدة.",
        lineQuantity: "الكمية",
        lineQuantityInvalid: "البند {{line}} يحتاج كمية غير صفرية.",
        lineRevenueAccount: "حساب الإيراد",
        lineRevenueRequired: "البند {{line}} يحتاج حساب إيراد.",
        lineTax: "الضريبة",
        lineTaxCode: "رمز الضريبة",
        lineTaxCodeRequired: "البند {{line}} يحتاج رمز الضريبة المحدد — المحرك يحسب المبلغ منه.",
        lineUnitPrice: "سعر الوحدة",
        linesSection: "البنود",
        loadError: "تعذر تحميل فواتير العملاء.",
        netPreview: "صافي البنود المدخلة: {{amount}} {{currency}}",
        netTotal: "الصافي",
        notFound: "تعذر تحميل هذه الفاتورة.",
        notes: "ملاحظات",
        numberAtFinalize: "يُخصص عند الاعتماد",
        openAmount: "المتبقي",
        postedAt: "رُحّلت",
        priceInvalid: "المبالغ بأربعة أرقام عشرية كحد أقصى.",
        quantityInvalid: "الكميات بستة أرقام عشرية كحد أقصى ولا يمكن أن تكون صفرًا.",
        removeLine: "حذف البند {{line}}",
        status: {
          draft: "مسودة",
          paid: "مسددة",
          partially_paid: "مسددة جزئيًا",
          posted: "مرحّلة",
          void: "ملغاة",
        },
        subtitle: "المسودات قابلة للتعديل؛ وما بعد المسودة مستند نظامي صادر.",
        taxCodeHint: "المحرك يحسب المبلغ من هذا الرمز ويحفظ لقطته.",
        taxPercent: "{{percent}}٪",
        taxTotal: "الضريبة",
        title: "فواتير العملاء",
      },
      depreciationRuns: {
        assetCount: "{{count}} أصل",
        empty: "لم يُشغَّل أي إهلاك بعد.",
        execute: "تشغيل الإهلاك",
        historySection: "السجل",
        period: "الفترة المالية",
        periodRequired: "اختر الفترة المالية.",
        runHint: "إعادة تشغيل فترة سبق ترحيلها آمنة: لا تجد شيئًا مستحقًا ولا ترحّل شيئًا.",
        runResult: "حُمّل {{assets}} أصلًا عبر {{journals}} قيدًا.",
        runSection: "تشغيل فترة",
        runSummary: "{{assets}} أصل · {{journals}} قيد",
        statusOption: {
          completed: "مكتمل",
          failed: "فشل",
          pending: "قيد التنفيذ",
        },
        subtitle: "تشغيل واحد لكل فترة مالية، مُلخَّص في قيد واحد لكل فئة أصول.",
        title: "تشغيلات الإهلاك",
      },
      fixedAssets: {
        accumulated: "الإهلاك المرحّل",
        accumulatedAfter: "المجمّع بعد البند",
        back: "العودة إلى السجل",
        capitalizationDate: "تاريخ الرسملة",
        capitalize: "رسملة أصل",
        capitalizeAction: "رسملة وتوليد الجدول",
        capitalizeSection: "رسملة أصل",
        charge: "القسط",
        class: "فئة الأصل",
        classRequired: "اختر فئة الأصل.",
        columns: {
          assetNo: "الأصل",
          capitalized: "تاريخ الرسملة",
          class: "الفئة",
          cost: "التكلفة",
          description: "الوصف",
          nbv: "صافي القيمة الدفترية",
          status: "الحالة",
        },
        cost: "تكلفة الاقتناء",
        costInvalid: "أدخل تكلفة الاقتناء بأربعة أرقام عشرية كحد أقصى.",
        currencyHint: "يجب أن تكون العملة الوظيفية للشركة — تُقيَّد الأصول بالتكلفة التاريخية.",
        decliningFactor: "معامل التناقص",
        description: "الوصف",
        descriptionRequired: "صف الأصل.",
        disposalDate: "تاريخ الاستبعاد",
        disposalDerecognized: "أُلغي الاعتراف بالإجمالي:",
        disposalGainLoss: "الربح أو الخسارة",
        disposalHint:
          "الربح أو الخسارة = المتحصلات ناقص صافي القيمة الدفترية، احتسابًا للإهلاك المرحّل فقط. الاستبعاد نهائي.",
        disposalSection: "استبعاد الأصل",
        dispose: "استبعاد وإلغاء الاعتراف",
        disposedNotice: "هذا الأصل مستبعد. الاستبعاد نهائي ولم يعد يُهلك.",
        empty: "لا توجد أصول مطابقة لهذه المرشحات.",
        factorInvalid: "معامل التناقص أكبر من 1 وحتى 5 كحد أقصى.",
        filterClass: "الفئة",
        filterStatus: "الحالة",
        inherit: "وراثة من الفئة",
        inheritHint: "اتركه فارغًا لوراثة إعداد الفئة الافتراضي.",
        lifeInvalid: "العمر الإنتاجي بين 1 و1200 شهر.",
        lineStatus: "الحالة",
        method: "طريقة الإهلاك",
        methodOption: {
          declining_balance: "القسط المتناقص",
          straight_line: "القسط الثابت",
        },
        nbv: "صافي القيمة الدفترية",
        newLife: "العمر الكلي المعدّل (أشهر)",
        newLifeHint: "العمر الكلي، شاملًا الفترات المرحّلة سابقًا.",
        newSalvage: "قيمة الخردة المعدّلة",
        notFound: "تعذر تحميل هذا الأصل.",
        period: "الفترة",
        proceeds: "المتحصلات",
        proceedsHint: "صفر في حالة الإتلاف.",
        proceedsInvalid: "أدخل المتحصلات — صفر في حالة الإتلاف — بأربعة أرقام عشرية كحد أقصى.",
        revise: "تعديل التقديرات",
        revisionEmpty: "التعديل يجب أن يغير العمر الإنتاجي أو قيمة الخردة أو كليهما.",
        revisionHint:
          "مستقبلي فقط. الفترات المرحّلة تبقى كما هي؛ ويوزَّع الرصيد غير المُهلك على ما تبقى.",
        revisionReason: "السبب",
        revisionResult:
          "بقيت {{posted}} فترة مرحّلة دون مساس، وأُعيد توليد {{replaced}} فترة مجدولة.",
        revisionSection: "تعديل التقديرات",
        salvage: "قيمة الخردة",
        salvageHint: "الافتراضي صفر. لا يُهلك الأصل دونها أبدًا.",
        salvageInvalid: "أدخل قيمة الخردة بأربعة أرقام عشرية كحد أقصى.",
        scheduleSection: "جدول الإهلاك",
        scheduleStatus: {
          posted: "مرحّل",
          scheduled: "مجدول",
        },
        scheduleWarning:
          "يُولَّد الجدول كاملًا الآن. وإذا لم يبلغ التقويم المالي نهاية العمر الإنتاجي يُرفض الطلب بدلًا من اقتطاع الجدول.",
        statusOption: {
          active: "نشط",
          disposed: "مستبعد",
          draft: "مسودة",
        },
        subtitle: "السجل بالتكلفة التاريخية، مع الإهلاك الذي رُحّل فعلًا.",
        switchToStraightLine: "التحول إلى القسط الثابت متى صار أعلى",
        title: "الأصول الثابتة",
        usefulLife: "العمر الإنتاجي (أشهر)",
      },
      gateway: {
        columns: {
          capturedAt: "تاريخ التحصيل",
          chargeId: "عملية الخصم",
          fee: "الرسوم",
          gross: "الإجمالي",
          net: "الصافي",
          provider: "المزود",
          status: "الحالة",
        },
        eventId: "حدث البوابة",
        exceptionReason: {
          ambiguous_match: "مطابقة ملتبسة",
          amount_mismatch: "اختلاف المبلغ",
          currency_mismatch: "اختلاف العملة",
          order_not_found: "الطلب غير موجود",
          provider_not_configured: "المزود غير مهيأ",
        },
        filterOpen: "العرض",
        filterStatus: "الحالة",
        noExceptions: "لا شيء ينتظر في قائمة الاستثناءات.",
        noPayments: "لا توجد عمليات خصم مطابقة لهذه المرشحات.",
        noPayouts: "لا توجد تسويات مطابقة لهذه المرشحات.",
        openOnly: "غير المعالجة فقط",
        paymentStatus: {
          exception: "استثناء",
          matched: "مطابقة",
          pending: "معلقة",
          settled: "مسواة",
        },
        payoutStatus: {
          pending: "معلق",
          reconciled: "مسوى",
        },
        reconcile: "تسوية التحويل",
        replay: "إعادة التشغيل",
        replayHint: "يعيد معالجة إشعار مخزّن. إعادة تسليم الحدث نفسه لا تُحدث أثرًا.",
        replaySection: "إعادة تشغيل حدث بوابة",
        resolutionDetail: "ما الذي تم",
        resolve: "إغلاق الاستثناء",
        resolvedAt: "عولج في",
        statementLine: "بند كشف الحساب",
        statementLineHint:
          "اختياري. تسميته تجعل ترحيل ذلك البند هو قيد التسوية بدلًا من إصدار قيد ثانٍ.",
        subtitle: "عمليات الخصم، والاستثناءات التي تعذّر نسبها، والتحويلات التي تنقلها إلى البنك.",
        tabExceptions: "الاستثناءات",
        tabPayments: "عمليات الخصم",
        tabPayouts: "التحويلات",
        title: "بوابة الدفع",
      },
      nav: {
        aging: "أعمار الذمم",
        assetClasses: "فئات الأصول",
        bankAccounts: "الحسابات البنكية",
        bankStatements: "كشوف الحسابات",
        customerInvoices: "فواتير العملاء",
        customerReceipts: "سندات القبض",
        depreciationRuns: "تشغيلات الإهلاك",
        fixedAssets: "الأصول الثابتة",
        gateway: "بوابة الدفع",
        group: "المالية",
        paymentRuns: "دفعات السداد",
        periodClose: "إقفال الفترة",
        periodEnd: "نهاية الفترة",
        bankRules: "قواعد البنك",
        statements: "القوائم المالية",
        proofs: "الإثباتات",
        statementLayouts: "تخطيطات القوائم",
        collections: "التحصيل",
        reconciliation: "التسوية البنكية",
        supplierInvoices: "فواتير الموردين",
        taxCodes: "الرموز الضريبية",
      },
      paymentRuns: {
        alreadyPosted: "هذه الدفعة رُحّلت بالفعل.",
        approvalRaised: "هناك طلب اعتماد مفتوح لهذه الدفعة.",
        back: "العودة إلى دفعات السداد",
        currencyRequired: "أدخل رمز عملة من ثلاثة أحرف.",
        debtorBic: "رمز BIC للمدين",
        debtorIban: "آيبان المدين",
        debtorName: "اسم حساب المدين",
        debtorRequired: "ملف السداد يحتاج اسم حساب المدين والآيبان ورمز BIC.",
        detailSubtitle: "تاريخ السداد {{date}}",
        discount: "الخصم",
        dueCutoffHint: "تُختار الفواتير داخل نافذة الخصم بغض النظر عن هذا التاريخ.",
        dueDate: "الاستحقاق",
        dueOnOrBefore: "مستحق في أو قبل",
        export: "تصدير ملف السداد",
        exportHint:
          "معيار ISO 20022 pain.001. المولّد حتمي، فإعادة التوليد تنتج الملف نفسه بايتًا ببايت.",
        exportSection: "ملف السداد البنكي",
        fileHash: "بصمة الملف المصدَّر",
        gross: "الإجمالي",
        invoice: "الفاتورة",
        linesSection: "الفواتير المختارة",
        net: "الصافي",
        noPayables: "لا توجد ذمم دائنة مرحّلة للاختيار منها.",
        notExported: "لم يُصدَّر بعد",
        notFound: "تعذر تحميل دفعة السداد هذه.",
        paymentDate: "تاريخ السداد",
        pickForbidden: "الاختيار اليدوي يحتاج صلاحية قراءة فواتير الموردين.",
        pickHint: "اترك الكل دون تحديد لاقتراح كل بند مستحق مؤهل.",
        pickSection: "الحصر في مجموعة مختارة يدويًا",
        post: "ترحيل الدفعة",
        postBlocked: "الترحيل مرفوض حتى تُعتمد الدفعة.",
        propose: "اقتراح دفعة",
        proposeSection: "اقتراح دفعة",
        selectedCount: "{{count}} فاتورة مختارة يدويًا",
        status: {
          approved: "معتمدة",
          cancelled: "ملغاة",
          draft: "مسودة",
          pending_approval: "بانتظار الاعتماد",
          posted: "مرحّلة",
          proposed: "مقترحة",
        },
        submissionReason: "ملاحظة",
        submit: "إرسال الدفعة",
        submitHint:
          "إذا لم تستلزم أي قاعدة خطوة اعتماد فسيعتمدها المحرك فورًا — راجع الحالة بعد الإرسال.",
        submitSection: "الإرسال للاعتماد",
        subtitle: "اقترح دفعة، وراجعها، ثم أرسلها للاعتماد.",
        supplier: "المورد",
        title: "دفعات السداد",
        total: "الإجمالي",
      },
      collections: {
        title: "التحصيل",
        subtitle: "كيف تُتابَع الفواتير المتأخرة، وكشف الحساب الذي يصل كل عميل عليه رصيد مفتوح.",
        ladderTitle: "سلّم التصعيد",
        ladderHint:
          "كل درجة تحدّد مدة التأخر والمبلغ المفتوح قبل إرسال ذلك الخطاب. حذف الدرجة يوقفها ولا يمحوها: خطاب أُرسل العام الماضي يذكر درجة يجب أن تبقى مفهومة.",
        noLevels: "لا توجد درجة مُعرّفة، فلا تتم أي متابعة.",
        level: "الدرجة",
        name: "الاسم",
        daysOverdue: "أيام التأخر",
        minOpen: "أدنى مبلغ مفتوح",
        template: "الخطاب",
        active: "نشطة",
        addLevel: "إضافة درجة",
        removeLevel: "حذف الدرجة",
        saveLadder: "حفظ السلّم",
        ladderSaved: "أصبح السلّم {{count}} درجة. ما حُذف أُوقف ولم يُمحَ.",
        ladderError: "تعذّر حفظ السلّم.",
        notEscalating: "درجة لاحقة لا تنتظر أكثر من سابقتها، فلن يُبلغها شيء أبدًا.",
        sweepTitle: "تشغيل المتابعة",
        sweepHint:
          "نفس المتابعة التي يشغّلها المجدول ليلًا. الفاتورة التي تُوبعت عند درجة لا تُتابع عندها مرة أخرى، فتشغيلها مرتين في اليوم لا يرسل شيئًا في المرة الثانية.",
        sweep: "جدولة المتابعة",
        sweepQueued:
          "جُدولت متابعة {{date}}. الفاتورة التي تُوبعت عند درجة لا تُتابع مجددًا، فإعادة التشغيل لا ترسل شيئًا.",
        sweepError: "تعذّرت جدولة المتابعة.",
        asOf: "التأخر حتى تاريخ",
        currency: "العملة",
        statementsTitle: "كشوف حسابات العملاء",
        statementsHint:
          "التجهيز والإرسال يجريان في الخلفية، لذا يعرض هذا ما أنجزه التشغيل حتى الآن بدل ادّعاء أن الكشوف أُرسلت.",
        sendStatements: "إرسال الكشوف",
        statementError: "تعذّر بدء تشغيل الكشوف.",
        runQueued: "التشغيل في الانتظار.",
        runCounts: "أُرسل {{sent}} من {{parties}} · فشل {{failed}}",
      },
      dunningTemplate: {
        dunning_level_1: "تذكير أول",
        dunning_level_2: "تذكير ثانٍ",
        dunning_level_3: "إشعار نهائي",
      },
      statementRunStatus: {
        pending: "في الانتظار",
        running: "جارٍ الإرسال",
        completed: "اكتمل",
        failed: "فشل",
      },
      proofs: {
        title: "الإثباتات",
        subtitle:
          "تقارير تشتق أرقامها مرتين وترفض الإجابة عند اختلافهما. ما تراه هنا قد فُحص بالفعل.",
        period: "الفترة",
        pickPeriod: "اختر فترة…",
        pickFirst: "اختر فترة.",
        taxTab: "الإقرار الضريبي",
        assetsTab: "سجل الأصول",
        grniTab: "مستلم بلا فاتورة",
        taxTitle: "الإقرار الضريبي",
        taxHint:
          "يُحتسب من القيود المُرحَّلة لا من المستندات، وعلى مستوى الشركة: الفرع موقع لكيان واحد بتسجيل ضريبي واحد.",
        taxCode: "الرمز",
        outputTax: "ضريبة المخرجات",
        inputTax: "ضريبة المدخلات",
        netTax: "الصافي",
        total: "الإجمالي",
        tieOut:
          "اشتُقت حركة كل حساب سيطرة بثلاث طرق واتفقت. {{untagged}} سطرًا مُرحَّلًا أصاب حساب ضريبة بلا رمز.",
        assetsTitle: "سجل الأصول الثابتة",
        assetsHint:
          "صافي القيمة الدفترية حتى نهاية الفترة. يُحكم بوجود الأصل بتاريخ استبعاده، فسجل مارس يُظهر أصلًا بيع في يونيو.",
        class: "الفئة",
        assetCount: "الأصول",
        cost: "التكلفة",
        depreciation: "الإهلاك المتراكم",
        netBookValue: "صافي القيمة الدفترية",
        tiesOut: "مطابق لحسابات السيطرة",
        registerVsLedger: "اشتُق السجل والدفتر كل على حدة واتفقا.",
        grniTitle: "مستلم بلا فاتورة",
        grniHint:
          "حساب المقاصة مقابل الاستحقاق الذي ما زالت تحمله سطور الاستلام. الطرفان من مصدرين مختلفين عمدًا — المطابقة التي تحسب أحدهما من الآخر لا تثبت شيئًا.",
        currency: "العملة",
        asOf: "حتى تاريخ",
        subledger: "من الاستلامات",
        ledger: "من الدفتر",
        accrued: "المستحق بالاستلام",
        cleared: "المُسوّى بالفواتير",
        agrees: "الطرفان متطابقان",
        disagrees: "الطرفان مختلفان",
        nothingOpen: "لا يوجد مستلم بلا فاتورة.",
        receiptLine: "سطر الاستلام",
        received: "المستلم",
        invoiced: "المفوتر",
        open: "المتبقي",
        withheld:
          "حُجب هذا التقرير لاختلاف اشتقاقيه. نشر رقم معقول مكانه هو ما يجعل دفاتر مكسورة تصمد حتى نهاية السنة.",
        error: "تعذّرت قراءة التقرير.",
      },
      statements: {
        title: "القوائم المالية",
        subtitle: "مُجمَّعة بالتخطيط الساري عند نهاية الفترة، فتبقى الفترة الأقدم كما كانت تُقرأ.",
        statement: "القائمة",
        period: "الفترة",
        pickPeriod: "اختر فترة…",
        pickFirst: "اختر فترة لقراءة القائمة.",
        layout: "التخطيط",
        inForce: "ما كان ساريًا",
        comparative: "المقارنة مع",
        priorYear: "السنة السابقة",
        noComparative: "بدون",
        line: "السطر",
        periodColumn: "هذه الفترة",
        yearToDate: "منذ بداية السنة",
        closing: "الرصيد الختامي",
        priorPeriod: "الفترة نفسها سابقًا",
        priorYtd: "سابقًا حتى تاريخه",
        accountCount: "{{count}} حساب",
        live: "محسوبة الآن",
        fromSnapshot: "كما قُدّمت عند الإقفال",
        assertionsTitle: "ما تم إثباته",
        assertionsHint:
          "يُنشر طرفا المعادلة ليتمكّن القارئ من إعادة الطرح. القائمة التي يفشل إثباتها لا تُصدر أصلًا.",
        generatedAt: "أُنتجت في {{time}}",
        blockedTitle: "لا يستطيع هذا التخطيط إصدارها بعد",
        blockedHint:
          "يجب أن يقع كل حساب في سطر واحد بالضبط. قبل ذلك ستُسقط القائمة رصيدًا بصمت، لذا لا تُصدر إطلاقًا.",
        error: "تعذّرت قراءة القائمة.",
      },
      assertion: {
        trial_balance_unbalanced: "المدين يساوي الدائن",
        balance_sheet_unbalanced: "الأصول تساوي الالتزامات وحقوق الملكية",
        cash_flow_does_not_reconcile: "الحركة تتطابق مع النقد",
      },
      statementLayouts: {
        title: "تخطيطات القوائم",
        subtitle:
          "كيف تُجمَّع القائمة. الحفظ يُنشئ إصدارًا جديدًا؛ والإصدار الذي استندت إليه قائمة مُقدَّمة يبقى كما هو.",
        versions: "التخطيطات والإصدارات",
        empty: "لا يوجد تخطيط مُعرّف.",
        pick: "اختر تخطيطًا لترى طريقة التجميع.",
        new: "تخطيط جديد",
        newVersion: "إصدار جديد من هذا",
        saveVersion: "حفظ كإصدار جديد",
        editorHint:
          "الحفظ يُنشئ إصدارًا. إعادة استخدام الرمز تُنشئ الإصدار التالي وتترك السابق كما هو تمامًا.",
        version: "إصدار {{version}}",
        from: "من {{date}}",
        code: "الرمز",
        type: "القائمة",
        name: "الاسم",
        notes: "ملاحظات",
        validFrom: "ساري من",
        provenance:
          "الرمز والنوع والإصدار هي ما تستند إليه القائمة المُقدَّمة، لذا لا يمكن تغييرها.",
        linesTitle: "السطور",
        line: "السطر",
        lineCode: "رمز السطر",
        label: "التسمية",
        ordinal: "الترتيب",
        kind: "مصدر القيمة",
        parent: "يُرحَّل إلى",
        noParent: "لا شيء (المستوى الأعلى)",
        activity: "النشاط",
        noActivity: "بلا نشاط",
        selects: "يختار",
        signFlip: "يُعرض موجبًا",
        signFlipLabel: "اعرضه موجبًا",
        ruleKind: "القاعدة",
        addRule: "إضافة قاعدة اختيار",
        removeRule: "حذف القاعدة",
        addLine: "إضافة سطر",
        removeLine: "حذف السطر",
        codeFrom: "من الرمز",
        codeTo: "إلى الرمز",
        accountType: "نوع الحساب",
        accountTag: "وسم الحساب",
        computedLine: "هذا السطر محسوب، فلا يربط حسابات — وهذا ما يمنع احتساب الحركة مرتين.",
        checkTitle: "هل يمكنه إصدار قائمة؟",
        checkHint:
          "كل حساب قابل للترحيل — وكل حساب موقوف ما زال يحمل حركة — يجب أن يقع في سطر واحد بالضبط. تُعرض كل الملاحظات دفعة واحدة.",
        check: "افحص هذا التخطيط",
        checkError: "تعذّر تنفيذ الفحص.",
        usable: "يمكن لهذا التخطيط إصدار قائمة.",
        blocked: "لا يمكن لهذا التخطيط إصدار قائمة بعد.",
        counts:
          "{{mapped}} من {{eligible}} حسابًا مرتبطًا · {{unmapped}} بلا ارتباط · {{untagged}} بلا وسم",
        saveError: "تعذّر حفظ التخطيط.",
        createError: "تعذّر حفظ التخطيط. تأكد أن لكل سطر رمزًا فريدًا.",
      },
      statementType: {
        profit_and_loss: "الأرباح والخسائر",
        balance_sheet: "المركز المالي",
        cash_flow: "التدفقات النقدية",
      },
      statementLineKind: {
        detail: "الحسابات التي يختارها",
        subtotal: "السطور التابعة له",
        current_year_earnings: "الأرباح غير الموزّعة",
        net_income: "صافي الدخل",
        cash: "أرصدة النقد",
        unclassified: "المتبقي التسوية",
      },
      statementRuleKind: {
        account_code_range: "نطاق رموز",
        account_type: "نوع الحساب",
        account_tag: "وسم الحساب",
      },
      cashFlowActivity: { operating: "تشغيلي", investing: "استثماري", financing: "تمويلي" },
      statementValidation: {
        account_unmapped: "حساب لا يقع في أي سطر",
        account_multiply_mapped: "حساب يقع في أكثر من سطر",
        account_wrong_statement: "حساب ينتمي إلى قائمة أخرى",
        required_line_missing: "سطر مطلوب مفقود",
        line_duplicated: "سطر مكرّر",
        line_kind_not_allowed: "هذه القائمة لا تحتوي هذا النوع من السطور",
        line_cannot_map_accounts: "سطر محسوب يحمل قواعد حسابات",
        activity_line_missing: "نشاط بلا سطر",
        activity_line_duplicated: "نشاط له أكثر من سطر",
        activity_missing: "حساب بلا وسم نشاط",
        subtotal_without_children: "مجموع فرعي لا يتبعه شيء",
        parent_cycle: "سطور تُرحّل إلى بعضها في حلقة",
        parent_not_subtotal: "سطر يُرحَّل إلى ما ليس مجموعًا فرعيًا",
        rule_invalid: "قاعدة اختيار غير صالحة",
        layout_empty: "التخطيط بلا سطور",
        cash_account_tagged: "حساب نقدي يحمل أيضًا وسم نشاط",
      },
      bankRules: {
        title: "قواعد البنك",
        subtitle: "ما الذي يحدث لسطر الكشف قبل أن يراه أحد.",
        postingTitle: "قواعد الترحيل التلقائي",
        postingHint:
          "تعمل قبل المطابقة، بترتيب التسلسل، وأول تطابق يفوز. وهي للسطور التي لم يُصدر لها مستند أصلًا — الرسوم والفوائد والعمولات.",
        noPosting: "لا توجد قاعدة ترحيل تلقائي.",
        newPosting: "قاعدة ترحيل تلقائي جديدة",
        editPosting: "تعديل قاعدة الترحيل التلقائي",
        postingFormHint:
          "تُجرَّب القواعد بالترتيب وأول تطابق يفوز. حدود المبلغ بالقيمة المطلقة، فتغطي القاعدة الواحدة الرسم سواء خُصم أو رُدّ.",
        referenceTitle: "قواعد المراجع",
        referenceHint:
          "تستخرج مرجع المستند من نص الكشف الحر ليجد المطابق ما يطابقه. تحدّد ما يُطابَق به السطر، لا ما يُرحَّل إليه.",
        noReference: "لا توجد قاعدة مراجع.",
        newReference: "قاعدة مراجع جديدة",
        editReference: "تعديل قاعدة المراجع",
        referenceFormHint:
          "الأولوية الأصغر تعمل أولًا. مجموعة الالتقاط هي جزء التطابق الذي يمثّل المرجع؛ و0 تعني التطابق كاملًا.",
        sequence: "الترتيب",
        priority: "الأولوية",
        rule: "القاعدة",
        name: "الاسم",
        matches: "يطابق",
        postsTo: "يُرحَّل إلى",
        pattern: "النمط",
        referencePattern: "نمط المرجع",
        counterparty: "نمط الطرف المقابل",
        target: "يشير إلى",
        group: "مجموعة الالتقاط",
        captureGroup: "المجموعة {{group}}",
        direction: "الاتجاه",
        minAmount: "الحد الأدنى",
        maxAmount: "الحد الأعلى",
        bankAccount: "الحساب البنكي",
        everyAccount: "كل الحسابات البنكية",
        otherAccount: "حساب بنكي آخر",
        noBankAccounts: "لا يوجد حساب بنكي.",
        pickAccount: "الحساب…",
        noAccounts: "لا يوجد حساب مطابق.",
        taxCode: "رمز الضريبة",
        noTax: "بدون ضريبة",
        noTaxCodes: "لا يوجد رمز ضريبة.",
        active: "نشط",
        inactive: "غير نشط",
        badPattern:
          "هذا النمط لا يُترجم. القاعدة غير القابلة للترجمة تُتجاهل بصمت، لذا تُرفض هنا بدلًا من ذلك.",
        unconstrained:
          "حدّد طرفًا مقابلًا أو مرجعًا أو حدًّا للمبلغ. القاعدة التي لا تقيّد شيئًا سترحّل كل سطر في الكشف.",
        saveError: "تعذّر حفظ القاعدة.",
        deleteError: "تعذّر حذف القاعدة.",
      },
      bankDirection: { inbound: "وارد", outbound: "صادر", any: "الاتجاهان" },
      bankDocumentType: {
        customer_receipt: "سند قبض",
        supplier_payment: "سند صرف",
        customer_invoice: "فاتورة عميل",
        supplier_invoice: "فاتورة مورّد",
        gateway_payout: "تحويل بوابة الدفع",
        posting_rule: "قاعدة ترحيل تلقائي",
      },
      periodEnd: {
        title: "نهاية الفترة",
        subtitle: "سجّل ما يتكرّر، وأعد تقييم ما هو بعملة أجنبية، وأقفل السنة.",
        period: "الفترة",
        pickPeriod: "اختر فترة…",
        recurringTitle: "القيود المتكرّرة",
        recurringHint:
          "تُنشئ القوالب قيدًا في يومها من كل شهر. لا يمكن الإنشاء مرتين: التشغيل الثاني يخبرك بما كان موجودًا.",
        noTemplates: "لا يوجد قالب متكرّر مُعرّف.",
        template: "القالب",
        schedule: "يعمل في",
        dayOfMonth: "اليوم {{day}}",
        window: "النطاق",
        reversal: "عكس تلقائي",
        reverses: "يُعكس في الفترة التالية",
        materialize: "إنشاء المستحقّ",
        materialized: "أُنشئ {{created}}، و{{existing}} كان موجودًا.",
        materializeError: "تعذّر إنشاء المسودات.",
        newTemplate: "قالب متكرّر جديد",
        editTemplate: "تعديل القالب المتكرّر",
        templateHint: "الموجب مدين. يجب أن يكون مجموع السطور صفرًا، والحفظ يستبدلها كاملة.",
        code: "الرمز",
        currency: "العملة",
        name: "الاسم",
        description: "الوصف",
        fixedAtCreation: "ثابت بعد إنشاء القالب.",
        dayLabel: "يوم الشهر",
        dayHint: "يُضبط إلى آخر يوم في الأشهر الأقصر.",
        startsOn: "يبدأ في",
        endsOn: "ينتهي في",
        endsOnHint: "اتركه فارغًا لقالب لا ينتهي.",
        autoReverse: "العكس في الفترة التالية",
        active: "نشط",
        inactive: "غير نشط",
        lines: "السطور",
        balance: "الرصيد",
        account: "الحساب",
        pickAccount: "الحساب…",
        noAccounts: "لا يوجد حساب مطابق.",
        amount: "المبلغ",
        lineDescription: "وصف السطر",
        addLine: "إضافة سطر",
        removeLine: "حذف السطر",
        unbalanced:
          "السطور غير متوازنة بمقدار {{amount}}. القالب غير المتوازن يُنشئ قيدًا لا يمكن ترحيله.",
        saveError: "تعذّر حفظ القالب.",
        fxTitle: "إعادة تقييم العملات الأجنبية",
        fxHint:
          "تُعيد تقييم الذمم والالتزامات والأرصدة البنكية المفتوحة بسعر الإقفال. إعادة التشغيل تعكس التشغيل السابق وتكتب تشغيلًا جديدًا؛ لا يُطمس شيء.",
        noRuns: "لم تُشغَّل أي إعادة تقييم بعد.",
        runNo: "التشغيل {{no}}",
        lineCount: "{{count}} مركز",
        revalue: "إعادة تقييم هذه الفترة",
        revalued: "بعد التقييم",
        revalueDone: "سجّل التشغيل {{no}} عدد {{count}} مركز.",
        revalueError: "تعذّرت إعادة التقييم. تحتاج سعر إقفال لكل عملة مفتوحة.",
        scope: "المركز",
        foreign: "المحتفظ به",
        rate: "سعر الإقفال",
        booked: "المسجّل",
        delta: "ربح / خسارة",
        yearTitle: "إقفال السنة",
        yearHint:
          "يرحّل أرصدة الإيرادات والمصروفات إلى الأرباح المبقاة. يختار النظام فترة الإقفال وحساب حقوق الملكية.",
        closeYear: "إقفال {{code}}",
        yearClosed: "رُحّل {{count}} حساب إلى الأرباح المبقاة.",
        yearCloseError: "تعذّر تنفيذ إقفال السنة.",
      },
      fxRunStatus: { completed: "سارٍ", reversed: "مُستبدل" },
      fxScope: { ap: "التزام", ar: "ذمة مدينة", bank: "رصيد بنكي" },
      periodClose: {
        advisoryTask: "استرشادية",
        automatedNotSignable: "المهمة الآلية لا تُعتمد يدويًا. يجيب عنها فحصها.",
        blocked: "ما زالت {{count}} مهمة تعترض الطريق.",
        blockingCodes: "المعترضات: {{codes}}",
        blockingTask: "معترضة",
        closeable: "لا شيء يعترض الطريق.",
        evidenceCount: "{{shown}} صفًا معروضًا من {{total}} صف تم العثور عليه",
        executeYearEnd: "ترحيل نتيجة السنة إلى الأرباح المبقاة",
        fiscalYear: "السنة المالية",
        note: "ملاحظة",
        period: "الفترة المالية",
        periodStatus: {
          closed: "مقفلة",
          closing: "قيد الإقفال",
          open: "مفتوحة",
        },
        reopen: "إعادة فتح الفترة",
        reopenHint: "رمز سبب مضبوط — سجل التدقيق لا يتراكم فيه نص حر.",
        reopenReason: "سبب إعادة الفتح",
        reopenReasonOption: {
          authorized_reopen: "إعادة فتح مصرح بها",
          controlled_correction: "تصحيح مضبوط",
          migration: "ترحيل بيانات",
        },
        retainedEarnings: "المُرحّل إلى الأرباح المبقاة",
        runChecks: "تشغيل كل الفحوصات الآلية",
        signOff: "اعتماد",
        subtitle: "قائمة الإقفال وأدلتها والخطوة الوحيدة التي يمكن للفترة أن تخطوها.",
        taskKind: {
          automated: "آلية",
          manual: "يدوية",
        },
        taskStatus: {
          failed: "أخفقت",
          open: "مفتوحة",
          passed: "اجتازت",
          signed_off: "معتمدة",
          skipped: "تعذر تشغيلها — ما زالت معترضة",
          waived: "متجاوَزة",
        },
        tasksSection: "قائمة الإقفال",
        title: "إقفال الفترة",
        transition: "الانتقال إلى {{status}}",
        waive: "تجاوز مع بيان السبب",
        waiverReasonRequired:
          "التجاوز يستلزم سببًا مكتوبًا — بدونه لا يستطيع أحد معرفة لماذا سُمح بالإقفال.",
        yearEndApprovalRequired: "أُحيل الإقفال للاعتماد. لن يُرحّل شيء حتى يصدر القرار.",
        yearEndCounts: "أُقفل {{accounts}} حسابًا · وزُرع {{openings}} رصيد افتتاحي",
        yearEndHint:
          "قيد واحد يقفل كل حسابات الإيراد والمصروف في الأرباح المبقاة. ويُثبت توازن ميزان المراجعة قبل الترحيل وبعده؛ والترحيل يقع مرة واحدة.",
        yearEndSection: "إقفال نهاية السنة",
        yearNotClosed: "لم تُقفل هذه السنة.",
      },
      permissions: {
        code: "403 · التحكم في الوصول",
        title: "الوصول إلى المالية مقيّد",
      },
      receipts: {
        allocate: "التخصيص",
        allocateTo: "التخصيص للفاتورة {{invoice}}",
        allocatedAmount: "المخصص",
        allocationHint:
          "هذه هي الخطة كاملة، لا تعديل عليها. وما تتركه دون تخصيص يُقيَّد على حساب العميل.",
        allocationInvalid: "أدخل مبالغ التخصيص بأربعة أرقام عشرية كحد أقصى.",
        allocationSection: "التخصيص على الفواتير المفتوحة",
        amount: "المبلغ المستلم",
        amountInvalid: "أدخل المبلغ المستلم بأربعة أرقام عشرية كحد أقصى.",
        back: "رجوع",
        bankAccount: "الحساب البنكي",
        bankRequired: "اختر الحساب البنكي الذي وصل إليه المال.",
        create: "تسجيل السند",
        createSubtitle: "تسجيل المال ينشئ مسودة. ويُرحّل السند عند تخصيصه.",
        createTitle: "تسجيل سند قبض من عميل",
        customer: "العميل",
        customerRequired: "اختر العميل.",
        detailsSection: "سند القبض",
        draftTitle: "سند قبض مسودة",
        dueDate: "الاستحقاق",
        enteredTotal: "جارٍ تخصيص",
        exceedsInvoice: "تخصيص {{amount}} للفاتورة {{invoice}} يتجاوز المبلغ المتبقي عليها.",
        exceedsReceipt: "مجموع التخصيصات يتجاوز المبلغ المستلم.",
        invoice: "الفاتورة",
        method: "طريقة السداد",
        methodOption: {
          bank_transfer: "تحويل بنكي",
          card: "بطاقة",
          cash: "نقدًا",
          cheque: "شيك",
          online_gateway: "بوابة إلكترونية",
          other: "أخرى",
        },
        noOpenInvoices: "لا توجد فواتير مفتوحة لهذا العميل.",
        notFound: "تعذر تحميل سند القبض هذا.",
        onAccount: "على الحساب",
        openAmount: "المتبقي",
        overpaymentNotice: "المتبقي دفعة زائدة على حساب العميل — وهي نتيجة مشروعة لا خطأ.",
        planApplied: "المطبق",
        planOnAccount: "على الحساب",
        planRemaining: "يبقى {{amount}} مفتوحًا",
        planSettled: "يسدد الفاتورة بالكامل",
        planTitle: "التخصيص المحفوظ",
        receivedOn: "تاريخ الاستلام",
        reference: "المرجع",
        remainder: "على الحساب بعد ذلك",
        saveAllocation: "حفظ التخصيص",
        settleInFull: "سداد كامل",
      },
      reconciliation: {
        addPart: "إضافة جزء",
        amount: "المبلغ",
        bankAccount: "الحساب البنكي",
        bookingDate: "تاريخ القيد",
        columns: {
          amount: "المبلغ",
          bookingDate: "تاريخ القيد",
          counterparty: "الطرف المقابل",
          reference: "المرجع",
          status: "الحالة",
          unmatched: "غير المطابق",
        },
        directionMismatch: "يجب أن يحرك كل جزء المال في الاتجاه نفسه الذي يحركه البند.",
        documentType: {
          customer_invoice: "فاتورة عميل",
          customer_receipt: "سند قبض",
          gateway_payout: "تحويل بوابة الدفع",
          posting_rule: "قاعدة ترحيل",
          supplier_invoice: "فاتورة مورد",
          supplier_payment: "سند صرف",
        },
        empty: "لا توجد بنود كشف مطابقة لهذه المرشحات.",
        exclude: "استبعاد من قائمة العمل",
        excludedNotice: "مستبعد: {{reason}}",
        exclusionReason: "سبب الاستبعاد",
        filterBankAccount: "الحساب البنكي",
        filterFrom: "مقيّد من",
        filterStatus: "الحالة",
        filterTo: "مقيّد إلى",
        lineSection: "بند الكشف",
        matchSection: "المطابقة والتجزئة",
        matched: "المطابق",
        noMatches: "لم يُطابَق شيء بهذا البند بعد.",
        noSuggestions: "لا مرشحات.",
        partAmount: "المبلغ",
        partAmountInvalid: "الجزء {{part}} يحتاج مبلغًا غير صفري.",
        partDocument: "المستند",
        partDocumentRequired: "الجزء {{part}} يحتاج مستندًا.",
        partType: "نوع المستند",
        partsRequired: "أضف جزءًا واحدًا على الأقل.",
        pass: "المرور {{pass}}",
        post: "ترحيل إلى الأستاذ",
        refreshSuggestions: "إعادة تشغيل محرك المطابقة",
        removePart: "حذف الجزء {{part}}",
        saveSplit: "استبدال التجزئة",
        split: "التجزئة",
        splitBalances: "مجموع الأجزاء يساوي البند تمامًا.",
        splitDoesNotSum: "يجب أن يساوي مجموع الأجزاء البند تمامًا. الفرق الحالي {{difference}}.",
        splitOutBy: "الفرق {{difference}}",
        status: "الحالة",
        statusOption: {
          excluded: "مستبعد",
          imported: "مستورد",
          matched: "مطابق",
          posted: "مرحّل",
          suggested: "مُرشَّح",
        },
        subtitle: "حركات كشف الحساب، وما تسدده، وما لم يُفسَّر بعد.",
        suggestions: "المرشحات",
        suggestionsHint:
          "الأقوى دليلًا أولًا. الترشيح ليس قرارًا — لا يُرحّل شيء حتى تُقبل المطابقة.",
        title: "التسوية البنكية",
        unmatched: "غير المطابق",
        useSuggestion: "إضافة إلى التجزئة",
      },
      supplierInvoices: {
        addLine: "إضافة بند",
        alreadyPosted: "هذه الفاتورة رُحّلت بالفعل.",
        amountPaid: "المسدد",
        backToList: "العودة إلى فواتير الموردين",
        capture: "قيد فاتورة",
        captureAction: "القيد والمطابقة",
        captureSubtitle:
          "تُحتسب الضريبة وتُشغَّل المطابقة الثلاثية في المعاملة نفسها التي يتم بها القيد.",
        captureTitle: "قيد فاتورة مورد",
        codingExpense: "حساب مصروف",
        codingHint: "البند إما هذا أو ذاك، ولا يجمع بينهما أبدًا.",
        codingPo: "بند أمر شراء",
        columns: {
          documentNo: "المستند",
          dueDate: "الاستحقاق",
          grandTotal: "الإجمالي",
          invoiceDate: "تاريخ الفاتورة",
          openFindings: "الملاحظات المفتوحة",
          status: "الحالة",
          supplier: "المورد",
          supplierNumber: "مرجع المورد",
        },
        detailSubtitle: "مرجع المورد {{reference}} · {{supplier}}",
        discountDays: "نافذة الخصم (أيام)",
        discountDaysInvalid: "نافذة الخصم بين 0 و365 يومًا.",
        discountInvalid: "أدخل خصم السداد المبكر كنسبة مئوية.",
        discountPercent: "نسبة خصم السداد المبكر",
        dueBeforeInvoice: "لا يجوز أن يسبق تاريخ الاستحقاق تاريخ الفاتورة.",
        dueDate: "تاريخ الاستحقاق",
        empty: "لا توجد فواتير موردين مطابقة لهذه المرشحات.",
        filterStatus: "الحالة",
        filterSupplier: "المورد",
        findingClosed: "مغلقة.",
        findingKind: {
          missing_receipt: "لا يوجد إشعار استلام",
          price_tolerance: "السعر خارج حد التسامح",
          qty_over_receipt: "مفوتر أكثر من المستلم",
        },
        findingLine: "البند {{line}} · {{description}}",
        findingReason: "السبب",
        findingStatus: {
          open: "مفتوحة",
          overridden: "متجاوَزة",
          resolved: "معالجة",
        },
        findingValues: "المتوقع {{expected}} · المفوتر {{actual}}",
        findingsSection: "ملاحظات المطابقة",
        grandTotal: "الإجمالي",
        grnLineHint: "تركه فارغًا يثير ملاحظة إيصال استلام مفقود.",
        headerSection: "الفاتورة",
        invoiceDate: "تاريخ الفاتورة",
        lineAccountRequired: "البند {{line}} يحتاج حساب مصروف.",
        lineCoding: "التوجيه المحاسبي",
        lineDescription: "الوصف",
        lineDescriptionRequired: "البند {{line}} يحتاج وصفًا.",
        lineExpenseAccount: "حساب المصروف",
        lineGrnLine: "بند إشعار الاستلام",
        lineHeading: "البند {{line}}",
        lineNet: "الصافي",
        linePoLine: "بند أمر الشراء",
        linePoLineRequired: "البند {{line}} يحتاج بند أمر الشراء الذي يفوتره.",
        linePriceInvalid: "البند {{line}} يحتاج سعر وحدة.",
        lineQuantity: "الكمية",
        lineQuantityInvalid: "البند {{line}} يحتاج كمية غير صفرية.",
        lineTax: "الضريبة",
        lineTaxCode: "رمز الضريبة",
        lineTotal: "الإجمالي",
        lineUnitPrice: "سعر الوحدة",
        linesSection: "البنود",
        netPreview: "صافي البنود المدخلة: {{amount}} {{currency}}",
        noFindings: "لم تجد المطابقة ما يستوجب ردًا.",
        noPurchaseOrder: "بدون أمر شراء",
        noReceipt: "بدون إيصال استلام (يثير ملاحظة)",
        notFound: "تعذر تحميل فاتورة المورد هذه.",
        numberRequired: "أدخل رقم فاتورة المورد نفسه — فهو ما يُفحص التكرار عليه.",
        override: "تجاوز — قبول الفرق",
        overridePending: "هذا التجاوز لدى سير عمل الاعتماد. تبقى الملاحظة مفتوحة حتى يصدر القرار.",
        post: "ترحيل إلى الأستاذ",
        postBlocked: "الترحيل مرفوض ما دامت هناك ملاحظة مطابقة مفتوحة. أغلق كل الملاحظات أولًا.",
        postedAt: "رُحّلت",
        priceInvalid: "الأسعار بستة أرقام عشرية كحد أقصى.",
        purchaseOrder: "أمر الشراء",
        purchaseOrderHint: "اختر أمرًا لتوجيه البنود إلى بنوده وإيصالات استلامه.",
        quantityInvalid: "الكميات بستة أرقام عشرية كحد أقصى ولا يمكن أن تكون صفرًا.",
        reasonRequired: "لا يمكن إغلاق ملاحظة دون سبب مكتوب.",
        rematch: "إعادة تشغيل المطابقة",
        removeLine: "حذف البند {{line}}",
        resolve: "معالجة — زال الفرق",
        status: {
          discrepancy: "فروقات",
          draft: "مسودة",
          matched: "مطابقة",
          paid: "مسددة",
          posted: "مرحّلة",
          void: "ملغاة",
        },
        subtitle: "فواتير الموردين المقيدة مع نتيجة المطابقة الثلاثية.",
        subtotal: "الصافي",
        supplier: "المورد",
        supplierNumber: "رقم فاتورة المورد",
        supplierNumberHint: "يُفحص التكرار لكل مورد دون حساسية لحالة الأحرف أو علامات الترقيم.",
        supplierRequired: "اختر المورد.",
        taxCodeHint: "ثبّت رمزًا، أو اترك المحرك يحدده.",
        taxDetermined: "يحدده المحرك",
        taxPercent: "{{percent}}٪",
        taxTotal: "الضريبة المستحقة للمورد",
        title: "فواتير الموردين",
      },
      taxCodes: {
        activeLabel: "متاح للمستندات الجديدة",
        addRate: "إضافة نسبة",
        addRateHint: "تغيير النسبة سطر جديد لا تعديل. ويُرفض تداخل فترات السريان للرمز الواحد.",
        code: "الرمز",
        codeFrozen: "مجمّد بعد الإنشاء — فالمستندات محفوظة تحته.",
        codeNameRequired: "الرمز الضريبي يحتاج رمزًا واسمًا.",
        columns: {
          active: "نشط",
          code: "الرمز",
          currentRate: "النسبة اليوم",
          jurisdiction: "النطاق الضريبي",
          kind: "النوع",
          name: "الاسم",
        },
        create: "إنشاء رمز ضريبي",
        createTitle: "رمز ضريبي جديد",
        editTitle: "تعديل {{code}}",
        empty: "لا توجد رموز ضريبية مطابقة لهذه المرشحات.",
        filterActive: "الحالة",
        filterJurisdiction: "النطاق الضريبي",
        inForce: "سارية اليوم",
        inputAccount: "حساب ضريبة المدخلات",
        inputAccountHint: "يُدين بترحيلات جانب المشتريات.",
        jurisdiction: "النطاق الضريبي",
        jurisdictionHint: "دولة بمعيار ISO، مع تقسيم فرعي اختياري (SA أو SA-01).",
        jurisdictionRequired: "حدد النطاق الضريبي.",
        kind: {
          exempt: "معفى",
          reverse_charge: "احتساب عكسي",
          standard: "قياسي",
          zero_rated: "نسبة صفرية",
        },
        kindHint: "يحدد عدد أطراف القيد التي يرحّلها الرمز. ومجمّد بعد الإنشاء.",
        kindLabel: "النوع",
        name: "الاسم",
        newCode: "رمز ضريبي جديد",
        noAccount: "غير مربوط",
        noRates: "لا توجد نسب بعد.",
        openEnded: "مفتوحة النهاية",
        openingRateHint:
          "النسبة الافتتاحية. يُنشأ الرمز ونسبته في معاملة واحدة، فلا يُحفظ رمز بلا نسبة أبدًا.",
        outputAccount: "حساب ضريبة المخرجات",
        outputAccountHint: "يُدان بترحيلات جانب المبيعات.",
        percent: "النسبة",
        percentInvalid: 'أدخل نسبة بين 0 و100 بستة أرقام عشرية كحد أقصى. القيمة "15" تعني 15٪.',
        ratesAfterCreate: "يظهر سجل النسب بعد إنشاء الرمز.",
        ratesTitle: "سجل النسب",
        reverseChargeAccounts: "رمز الاحتساب العكسي يرحّل الطرفين، فيلزمه حسابا المراقبة معًا.",
        save: "حفظ الرمز الضريبي",
        subtitle: "الرموز الضريبية للشركة وحسابات مراقبتها وسجل نسبها المؤرخ بالسريان.",
        title: "الرموز الضريبية",
        validFrom: "سارية من",
        validFromRequired: "النسبة تحتاج تاريخ بدء سريانها.",
        validRangeInvalid: "لا يجوز أن يسبق تاريخ الانتهاء تاريخ البدء.",
        validTo: "سارية حتى",
        validToHint: "اتركه فارغًا لنسبة مفتوحة النهاية.",
        zeroRateRequired: "الرمز صفري النسبة أو المعفى يجب أن يحمل نسبة صفرية.",
      },
    },
  },
} as const;

export type FinanceMessages = typeof financeMessages;
